import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
  output
} from '@angular/core';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { F8964EleService } from '../../services/f8964-ele.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { toIsoDateString } from '../../../shared/_utils/date-utils';
// A single editable attribute within a form line group.
type F8964FieldEditType = 'text' | 'number' | 'radio' | 'checkbox' | 'dropdown' | 'date' | 'readonly';
type F8964LookupType = 'country' | 'currency' | 'principal-activity-code' | 'state';

interface F8964EleField {
  attribName: string;
  lineAttribKey: string;
  objectId: string;
  propertyType: string;
  value: any;
  isChanged: boolean;
  fieldLabel: string;
  isEditableFlag: string;
  editType: F8964FieldEditType;
  dropdownOptions: Array<{ code: string; desc: string }>;
  /** Original API row order — keeps non-address fields stable in display. */
  rowOrder: number;
}

type F8964TableMode = 'standard' | 'part3' | 'part4';
type F8964RowKind = 'form-header' | 'section-header' | 'subsection-header' | 'instruction-note' | 'data' | 'multi-col' | 'part4-line1' | 'part4-line1-item';

interface F8964EleRenderLine {
  kind: F8964RowKind;
  lineNo: string;
  label: string;
  fields: F8964EleField[];
  formLineKey: string;
}

interface F8964EleSection {
  mode: F8964TableMode;
  lines: F8964EleRenderLine[];
}

/** PDF section titles when API omits attrib_value description (fir-5471-schedule-o pattern). */
const SECTION_LABELS: Record<string, string> = {
  F8964_ELE_HEADER: 'Form 8964-ELE - Section 987 Elections',
  F8964_ELE_PartI: 'Part I — Identifying Information of Owner and QBU',
  F8964_ELE_I_A: 'Section A — Owner information',
  F8964_ELE_I_B: 'Section B — QBU information',
  F8964_ELE_II_PartII: 'Part II — Listing of QBUs for Authorized Persons With Multiple Owners and/or Multiple QBUs',
  F8964_ELE_II_A: 'Section A — QBU information. For authorized persons with multiple QBUs subject to the consistency rules in Regulations section 1.987-1(g)(2). See instructions.',
  F8964_ELE_II_B: 'Section B — QBU information. For authorized persons with multiple QBUs not subject to the consistency rules in Regulations section 1.987-1(g)(2). See instructions.',
  F8964_ELE_III_PartIII: 'Part III — Type of Election (see instructions)',
  F8964_ELE_IV_PartIV: 'Part IV — Other Information'
};

/** Row/field descriptions keyed by attrib_name when API description is empty. */
const ATTRIB_LABELS: Record<string, string> = {
  F8964_ELE_NA: 'Does the authorized person have multiple owners and/or multiple QBUs?',
  F8964_ELE_I_A_line1: 'For the owner of the QBU, if different than the authorized person, provide the following (see instructions).',
  F8964_ELE_I_A_line1a: 'Name',
  F8964_ELE_I_A_line1b: 'Annual accounting period and tax year of the owner when election or revocation is made (see instructions)',
  F8964_ELE_I_A_line1c: 'Address',
  'F8964_ELE_I_A_line1c(i)': 'Identifying number, if any',
  'F8964_ELE_I_A_line1c(ii)': 'Reference ID number (see instructions)',
  'F8964_ELE_I_A_line1c(iii)': 'Name and address (including corporate department, if applicable) of person(s) with custody of the books and records of the QBUs',
  'F8964_ELE_I_A_line1c(iv)': 'Country under whose laws organized',
  F8964_ELE_I_A_line1g: 'Functional currency',
  F8964_ELE_I_A_line2: 'For the direct owner of the QBU (if different from the owner), provide the following (see instructions).',
  F8964_ELE_I_A_line2a: 'Name',
  F8964_ELE_I_A_line2b: 'Country under whose laws organized',
  F8964_ELE_I_A_line2c: 'Address',
  F8964_ELE_I_A_line2d: 'Functional currency',
  F8964_ELE_I_B_Line1: 'Provide the following information for the QBU subject to elections.',
  F8964_ELE_I_B_Line1a: 'Name of QBU',
  'F8964_ELE_I_B_Line1b(i)': 'Identifying number, if any',
  'F8964_ELE_I_B_Line1b(ii)': 'Reference ID number (see instructions)',
  F8964_ELE_I_B_Line1c: 'Address of QBU',
  'F8964_ELE_I_B_Line1c(i)': 'For QBU, country(ies) under whose laws organized and entity type under local tax law',
  'F8964_ELE_I_B_Line1c(ii)': 'Date(s) of organization',
  'F8964_ELE_I_B_Line1c(iii)': 'Effective date as QBU',
  'F8964_ELE_I_B_Line1c(iv)': 'Country(ies) in which principal business activity is conducted',
  F8964_ELE_I_B_Line1h: 'Principal business activity code number',
  F8964_ELE_I_B_Line1i: 'Principal business activity',
  F8964_ELE_I_B_Line1j: 'Functional currency',
  F8964_ELE_I_B_Line2: 'Provide the following information for the owner\'s accounting period stated above.',
  F8964_ELE_I_B_Line2a: 'Name of QBU office or agent (if any) in the United States',
  'F8964_ELE_I_B_Line2b(i)': 'Identifying number',
  'F8964_ELE_I_B_Line2b(ii)': 'Reference ID number',
  F8964_ELE_I_B_Line2c: 'Address',
  F8964_ELE_I_B_Line2d: 'Name and address (including corporate department, if applicable) of person(s) with custody of the books and records of the QBU, and the location of such books and records, if different',
  F8964_ELE_II_A_Line1a: 'Is the authorized person a U.S. person other than a partnership?',
  F8964_ELE_II_A_Line1b: 'Is the authorized person a domestic partnership?',
  F8964_ELE_II_A_Line2: 'List the QBUs of Controlled Foreign Corporations (CFCs) that are required to make elections consistently with the authorized person under Regulations section 1.987-1(g)(2)(ii). Attach statement.',
  F8964_ELE_II_A_Line3: 'List the QBUs of foreign partnerships that are required to make elections consistently with the authorized person under Regulations section 1.987-1(g)(2)(ii). Attach statement.',
  F8964_ELE_II_A_Line4: 'Other information. Attach statement.',
  F8964_ELE_II_B_Line1: 'List any QBUs not listed in Section A for which the authorized person is making the elections in Part III. Attach statement.',
  F8964_ELE_II_B_Line2: 'Other information. Attach statement.',
  F8964_ELE_IV_Line1a: 'Name and title of the representative of authorized person making election whom the IRS may call for more information',
  F8964_ELE_IV_Line1b: 'Representative\'s telephone number',
  F8964_ELE_IV_Line2: 'For CFCs listed in Part I, Section A, Line 1, or Part II, Section A, Line 2, list Controlling Domestic Shareholders and EIN.'
};

/**
 * The 7rc5om response still carries stale line_no / attrib_name values for a few
 * F8964-ELE rows. Keep the original backend identifiers for save parity, but
 * render PDF line numbers here.
 */
const PDF_LINE_NO_OVERRIDES: Record<string, string> = {
  'F8964_ELE_I_A_line1c(i)': 'd(1)',
  'F8964_ELE_I_A_line1c(ii)': 'd(2)',
  'F8964_ELE_I_A_line1c(iii)': 'e',
  'F8964_ELE_I_A_line1c(iv)': 'f',
  F8964_ELE_I_A_line1g: 'g',
  F8964_ELE_I_A_line2: '2',
  F8964_ELE_I_A_line2a: '2a',
  F8964_ELE_I_A_line2b: '2b',
  F8964_ELE_I_A_line2c: '2c',
  F8964_ELE_I_A_line2d: '2d',
  F8964_ELE_I_B: '',
  F8964_ELE_I_B_Line1: '1',
  F8964_ELE_I_B_Line1a: '1a',
  'F8964_ELE_I_B_Line1b(i)': '1b(1)',
  'F8964_ELE_I_B_Line1b(ii)': '1b(2)',
  F8964_ELE_I_B_Line1c: '1c',
  'F8964_ELE_I_B_Line1c(i)': '1d',
  'F8964_ELE_I_B_Line1c(ii)': '1e',
  'F8964_ELE_I_B_Line1c(iii)': '1f',
  'F8964_ELE_I_B_Line1c(iv)': '1g',
  F8964_ELE_I_B_Line1h: '1h',
  F8964_ELE_I_B_Line1i: '1i',
  F8964_ELE_I_B_Line1j: '1j',
  F8964_ELE_I_B_Line2: '2',
  F8964_ELE_I_B_Line2a: '2a',
  'F8964_ELE_I_B_Line2b(i)': '2b(1)',
  'F8964_ELE_I_B_Line2b(ii)': '2b(2)',
  F8964_ELE_I_B_Line2c: '2c',
  F8964_ELE_I_B_Line2d: '2d',
  F8964_ELE_II_PartII: '',
  F8964_ELE_II_A: '',
  F8964_ELE_II_A_Line1a: '1a',
  F8964_ELE_II_A_Line1b: '1b',
  F8964_ELE_II_A_Line2: '2',
  F8964_ELE_II_A_Line3: '3',
  F8964_ELE_II_A_Line4: '4',
  F8964_ELE_II_B: '',
  F8964_ELE_II_B_Line1: '1',
  F8964_ELE_II_B_Line2: '2'
};

/**
 * Backend-only stale rows that are not present on the 12-2025 PDF. They remain
 * documented here instead of being remapped to PDF lines.
 */
const STALE_BACKEND_ROWS = new Set<string>([
  'F8964_ELE_I_A_line1d(i)',
  'F8964_ELE_I_A_line1d(ii)',
  'F8964_ELE_I_A_line1e',
  'F8964_ELE_I_A_line1f',
  'F8964_ELE_I_A_line2c(i)',
  'F8964_ELE_I_A_line2c(ii)',
  'F8964_ELE_I_A_line2c(iii)',
  'F8964_ELE_I_A_line2c(iv)',
  'F8964_ELE_I_B_Line1d',
  'F8964_ELE_I_B_Line1e',
  'F8964_ELE_I_B_Line1f',
  'F8964_ELE_I_B_Line1g',
  'F8964_ELE_I_B_Line2c(i)',
  'F8964_ELE_I_B_Line2c(ii)',
  'F8964_ELE_I_B_Line2c(iii)',
  'F8964_ELE_I_B_Line2c(iv)'
]);

const PART3_ELECTION_LABELS: Record<string, string> = {
  Line1: 'Section 987 Current Rate Election—Regulations section 1.987-1(d)(2)',
  Line2: 'Section 987 Annual Recognition Election—Regulations section 1.987-5(b)(2)',
  Line3: 'Section 988 Mark-to-Market Election—Regulations section 1.987-3(b)(4)(ii)',
  Line4: 'Section 987 Grouping Election—Regulations section 1.987-1(b)(3)(ii)',
  Line5: 'Election To Use a Spot Rate Convention—Regulations section 1.987-1(c)(1)(ii)',
  Line6: 'Election To Use the Historic Inventory Method—Regulations section 1.987-3(c)(2)(iv)(B)',
  Line7: 'Amortization Election in the Transition Rules—Regulations section 1.987-10(e)(5)(ii)(A)',
  Line8: 'Small Business Election in the Transition Rules—Regulations section 1.987-10(e)(7)(iv)',
  Line9: 'Election To Characterize Certain Section 987 Gain/(Loss) as Foreign Currency Gain/(Loss) From Section 988 Transactions—Regulations section 1.987-6(b)(2)(i)(C)(1)',
  Line10: 'Recurring Transfer Group Election—Proposed Regulations section 1.987-2(f)(1)'
};

const FIELD_SUFFIX_LABELS: Record<string, string> = {
  Ident_num: 'Identifying number',
  EIN: 'Identifying number',
  'Effec_ ele': 'Effective date of the election',
  Effec_ele: 'Effective date of the election',
  EFFECTIVE_DATE_ELE: 'Effective date of the election',
  'Effec_ rev': 'Effective date of the revocation',
  Effec_rev: 'Effective date of the revocation',
  EFFECTIVE_DATE_REV: 'Effective date of the revocation',
  Input_che: 'Election made',
  Name_CFC: '(a) Name of CFC',
  NAME_1: 'Name line 1',
  NAME_2: 'Name line 2',
  Iden_Num: '(b) Identifying number',
  Refe_ID: '(c) Reference ID number',
  REF_ID: '(c) Reference ID number',
  Name_Sha: '(d) Name of U.S. Shareholder',
  USSH_NAME_1: 'Name line 1',
  USSH_NAME_2: 'Name line 2',
  TIN_Sha: '(e) TIN of U.S. Shareholder',
  USSH_TIN: '(e) TIN of U.S. Shareholder',
  Perc_Sto: '(f) Percent of voting stock',
  VOTING_STOCK: '(f) Percent of voting stock'
};

const PART3_FIELD_ORDER = ['Ident_num', 'Effec_ele', 'Effec_rev', 'Input_che'];
const PART4_FIELD_ORDER = ['Name_CFC', 'Iden_Num', 'Refe_ID', 'Name_Sha', 'TIN_Sha', 'Perc_Sto'];
const PART4_FIELD_SORT_ORDER = [
  'NAME_1', 'NAME_2', 'Name_CFC', 'EIN', 'Iden_Num', 'REF_ID', 'Refe_ID',
  'USSH_NAME_1', 'USSH_NAME_2', 'Name_Sha', 'USSH_TIN', 'TIN_Sha', 'VOTING_STOCK', 'Perc_Sto'
];
const PART3_MULTI_SUFFIXES = [
  'Ident_num', 'EIN', 'Effec_ele', 'Effec_rev', 'EFFECTIVE_DATE_ELE', 'EFFECTIVE_DATE_REV',
  'Effec_ ele', 'Effec_ rev', 'Input_che', 'III_NA_LINE'
];
const PART3_FIELD_SUFFIX_ALIASES: Record<string, string[]> = {
  Ident_num: ['Ident_num', 'EIN'],
  Effec_ele: ['Effec_ele', 'EFFECTIVE_DATE_ELE', 'Effec_ ele'],
  Effec_rev: ['Effec_rev', 'EFFECTIVE_DATE_REV', 'Effec_ rev'],
  Input_che: ['Input_che']
};
const PART4_COLUMN_SUFFIX_ALIASES: Record<string, string[]> = {
  Name_CFC: ['NAME_1', 'NAME_2', 'Name_CFC'],
  Iden_Num: ['EIN', 'Iden_Num'],
  Refe_ID: ['REF_ID', 'Refe_ID'],
  Name_Sha: ['USSH_NAME_1', 'USSH_NAME_2', 'Name_Sha'],
  TIN_Sha: ['USSH_TIN', 'TIN_Sha'],
  Perc_Sto: ['VOTING_STOCK', 'Perc_Sto']
};

/** PDF "If Yes/No…" guidance shown after specific questions (Form 8964-ELE PDF). */
const INSTRUCTION_NOTES: Record<string, string[]> = {
  F8964_ELE_II_A_Line1a: [
    'If "Yes," list the QBUs owned by the authorized person. Attach statement.'
  ],
  F8964_ELE_II_A_Line1b: [
    'If "Yes," list the QBUs owned by the authorized person. Attach statement.'
  ]
};

const PART_II_INTRO_NOTE =
  'Complete Sections A and B and attach statements for authorized persons with multiple owners and QBUs. See instructions.';

@Component({
  selector: 'gtw-f8964-ele-ce',
  templateUrl: './f8964-ele.component.html',
  styleUrl: './f8964-ele.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.f8964-ele--minimised]': 'minimise'
  }
})
export class F8964EleComponent implements OnInit, OnDestroy {
  readonly requestGridUpdate = output<string>({ alias: 'request-grid-update' });

  @Input('row-data')
  set _row_data(rowData: string | any) {
    try {
      const data = typeof rowData === 'string' ? JSON.parse(rowData) : rowData;
      if (data && Object.keys(data).length > 0) {
        this.rowData = data;
        this.applyEntityMetadata();
        this.resetAndInit();
      }
    } catch {
      return;
    }
  }

  private isInitialized = false;
  private formLoadCancel$ = new Subject<void>();
  private lookupCancel$ = new Subject<void>();
  private subscriptions = new Subscription();

  rowData: any = {};
  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  globalServiceWrapper: any = {};

  modalTitle = 'Section 987 Elections';
  minimise = false;

  isSaveClicked = false;
  crudLoading = false;

  loading = false;
  errorMessage = '';
  formData: any = null;
  formTitle = '';
  formSections: F8964EleSection[] = [];
  private originalFormSections: F8964EleSection[] = [];

  taxYear = '';
  entityList: { key: string; value: any }[] = [];
  countries: any[] = [];
  states: any[] = [];
  currencies: any[] = [];
  principalBusinessActivityList: any[] = [];
  functionalCurrency = '';
  principalActivityCode = '';
  principalBusinessActivityDesc = '';
  //filer added
  filingGroup: any[] = [];
  selectedGroup: any = null;
  part4VisibleRowCount = 1;
  /** Part IV Line 2 shareholder rows hidden by delete (indices into getPart4ShareholderLines()). */
  private part4HiddenRowIndices = new Set<number>();
  /** Hidden Part IV rows the user deleted. Persisted rows are sent as removerows on save. */
  private part4DeletedRowIndices = new Set<number>();
  part4ColumnHeaders: string[] = PART4_FIELD_ORDER.map(key => FIELD_SUFFIX_LABELS[key] || key);
  private rawFormRows: any[] = [];
  /** Stable US vs foreign address mode per line — only changes when country dropdown changes or form reloads. */
  private foreignAddressLineKeys = new Set<string>();

  private readonly defaultOverlayHeight = '90vh';

  private readonly f8964EleService = inject(F8964EleService);
  private readonly gtwGlobal = inject(GlobalService);
  private readonly gtwWorkspace = inject(WorkspaceService);
  private readonly jsonObjectFactory = inject(JsonObjectFactoryService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly snackbar = inject(GtwSnackbarService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndInit();

    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.formLoadCancel$.next();
    this.formLoadCancel$.complete();
    this.lookupCancel$.next();
    this.lookupCancel$.complete();
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };

    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.globalServiceWrapper = {
      globalParams: {
        ...extractObject(this.gtwGlobal.global?.globalParams || {}),
        ...extractObject(hydrated)
      },
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.global?.inputs
    };

    const rowObj = rowPayloadFromActiveScreen(activeScreen);
    const resolved = Array.isArray(rowObj)
      ? extractObject(rowObj[0]?.data ?? rowObj[0])
      : extractObject(rowObj);
    if (Object.keys(resolved).length > 0) {
      this.rowData = resolved;
    }
    this.applyEntityMetadata();
  }

  private applyEntityMetadata(): void {
    this.taxYear = String(this.rowData?.TAX_YEAR ?? '');
    this.entityList = [
      { key: 'Tax Year', value: this.rowData?.TAX_YEAR || '' },
      { key: 'LEID', value: this.rowData?.LEID || '' },
      { key: 'LE Name', value: this.rowData?.LE_NAME || '' }
    ];
    this.functionalCurrency = this.rowData?.FUNCTIONAL_CURRENCY
      || this.rowData?.HO_FUNCTIONAL_CURRENCY
      || '';
    this.principalActivityCode = this.rowData?.PRINCIPAL_ACTIVITY_CODE
      || this.rowData?.PRINCIPAL_BUSINESS_ACTIVITY_CODE
      || '';
    this.principalBusinessActivityDesc = this.rowData?.PRINCIPAL_BUSINESS_ACTIVITY
      || this.rowData?.PRINCIPAL_BUSINESS_ACTIVITY_DESC
      || '';
    this.syncPrincipalBusinessActivityDesc();
  }

  private resetAndInit(): void {
    this.isInitialized = false;
    this.init();
  }

  init(): void {
    if (
      this.isInitialized ||
      !this.rowData ||
      Object.keys(this.rowData).length === 0 ||
      !this.GENERAL_CONFIG?.base_url
    ) {
      return;
    }
    this.isInitialized = true;
    this.loadLookups();
    this.loadFormData();
  }

  private getTaxYear(): string {
    return String(
      this.filterParams?.tax_year
      || this.globalServiceWrapper?.globalParams?.tax_year
      || this.rowData?.TAX_YEAR
      || ''
    );
  }

  /** Load lookup data via jsonObjectFactory — same path as F8858 schd-c1 / form load (merges filterParams). */
  private loadLookupJson(actionCode: string): Observable<any> {
    const base = this.GENERAL_CONFIG.base_url;
    return this.jsonObjectFactory.getJSONObj(
      `${base}/loadJsonObject`,
      {
        action_code: actionCode,
        tax_year: this.getTaxYear(),
        jcd_key: this.resolveJcdKey()
      },
      this.globalServiceWrapper,
      null,
      null
    );
  }

  /** Assign lookup arrays; supports direct arrays and nested jsonObject shapes. */
  private assignLookupArray(target: 'countries' | 'currencies' | 'states' | 'principalBusinessActivityList', response: any): void {
    if (!this.isApiSuccess(response)) {
      return;
    }
    this[target] = this.extractLookupList(response);
  }

  private isApiSuccess(response: any): boolean {
    return String(response?.callSuccess) === '1';
  }

  private extractLookupList(response: any): any[] {
    if (!this.isApiSuccess(response)) {
      return [];
    }
    let payload = response?.jsonObject;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return [];
      }
    }
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && typeof payload === 'object') {
      const nested = payload.currencies
        ?? payload.currencyList
        ?? payload.rows
        ?? payload.data
        ?? payload.items;
      if (Array.isArray(nested)) {
        return nested;
      }
    }
    return [];
  }

  private loadLookups(): void {
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }

    this.lookupCancel$.next();

    const base = this.GENERAL_CONFIG.base_url;
    const jcdKey = this.resolveJcdKey();
    const taxYear = this.getTaxYear();
    const lookupParams = { tax_year: taxYear, jcd_key: jcdKey };

    //filer added
    const filingGroupParams = {
      ...(this.globalServiceWrapper?.globalParams || {}),
      ...(this.filterParams || {}),
      ...lookupParams,
      action_code: 'f5sne6'
    };

    forkJoin({
      countries: this.loadLookupJson('kbuwei'),
      currencies: this.loadLookupJson('3ucwjb'),
      states: this.loadLookupJson('6eyqnz'),
      principal: this.loadLookupJson('y5ypqx'),
      //filer added
      filingGroup: this.jsonObjectFactory.getJSONObj(
        `${base}/loadJsonObject`,
        filingGroupParams,
        this.globalServiceWrapper,
        null,
        null
      )
    }).pipe(takeUntil(this.lookupCancel$))
      .subscribe(
        (res: any) => {
          this.ngZone.run(() => {
            this.assignLookupArray('countries', res.countries);
            this.assignLookupArray('currencies', res.currencies);
            this.assignLookupArray('states', res.states);
            this.assignLookupArray('principalBusinessActivityList', res.principal);
            //filer added
            this.filingGroup = this.extractLookupList(res.filingGroup);
            this.resolveSelectedFilingGroup();
            this.syncPrincipalBusinessActivityDesc();
            this.refreshFieldDropdownOptions();
            this.syncLinkedLookupFieldValues();
            this.normalizeAllFieldBindings();
            this.originalFormSections = JSON.parse(JSON.stringify(this.formSections));
            this.cdr.detectChanges();
            setTimeout(() => this.cdr.detectChanges());
          });
        },
        (err: any) => console.error('Error loading F8964 ELE lookups:', err)
      );
  }

  //filer added
  private resolveSelectedFilingGroup(): void {
    if (!this.filingGroup?.length) {
      this.selectedGroup = null;
      return;
    }

    const rd = this.rowData || {};
    const candidateIds = [
      rd.LEGAL_ENTITY_ID,
      rd.FILING_LEGAL_ENTITY_ID,
      rd.CONSOL_GROUP_KEY,
      rd.LEID
    ]
      .filter((value: any) => value !== undefined && value !== null && value !== '')
      .map((value: any) => String(value));

    for (const id of candidateIds) {
      const match = this.filingGroup.find((fg: any) =>
        String(fg.LEGAL_ENTITY_ID) === id
        || String(fg.LEID) === id
        || String(fg.CONSOL_GROUP_KEY) === id
      );
      if (match) {
        this.selectedGroup = match;
        return;
      }
    }

    this.selectedGroup = this.filingGroup[0];
  }

  formatFilerDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  getAuthorizedPersonName(): string {
    return this.rowData?.LE_NAME
      || this.rowData?.LEGAL_ENTITY_NAME
      || '';
  }

  getAuthorizedPersonIdentifyingNumber(): string {
    return this.rowData?.EIN
      || this.rowData?.TIN
      || this.rowData?.IDENTIFYING_NUMBER
      || this.rowData?.TAX_ID
      || '';
  }

  getAuthorizedPersonReferenceId(): string {
    return this.rowData?.REFERENCE_ID
      || this.rowData?.REF_ID
      || this.rowData?.REFERENCE_ID_NUMBER
      || '';
  }

  getAuthorizedPersonAddressLine1(): string {
    return this.rowData?.ADDR_LINE1
      || this.rowData?.ADDRESS_LINE1
      || this.rowData?.STREET_ADDRESS
      || '';
  }

  getAuthorizedPersonAddressLine2(): string {
    return this.rowData?.ADDR_LINE2
      || this.rowData?.ADDRESS_LINE2
      || this.rowData?.CITY_STATE_ZIP
      || [
        this.rowData?.CITY,
        this.rowData?.STATE,
        this.rowData?.ZIP
      ].filter(Boolean).join(', ');
  }
 //filer added end
  private syncPrincipalBusinessActivityDesc(): void {
    if (!this.principalActivityCode || !this.principalBusinessActivityList.length) {
      return;
    }
    const match = this.principalBusinessActivityList.find(
      (a: any) => String(a.ACTIVITY_CODE) === String(this.principalActivityCode)
        || a.ACTIVITY_DESC === this.principalActivityCode
    );
    if (match) {
      this.principalActivityCode = match.ACTIVITY_CODE;
      this.principalBusinessActivityDesc = match.ACTIVITY_DESC;
    }
  }

  onPrincipalActivityCodeChange(activityCode: string): void {
    this.principalActivityCode = activityCode || '';
    const match = this.principalBusinessActivityList.find(
      (a: any) => String(a.ACTIVITY_CODE) === String(activityCode)
    );
    this.principalBusinessActivityDesc = match?.ACTIVITY_DESC || '';
    this.updatePrincipalActivityDescFields(activityCode, true);
    this.syncPrincipalActivityCodeFields(activityCode, true);
    this.cdr.detectChanges();
  }

  private syncPrincipalActivityCodeFields(activityCode: string, markChanged = false): void {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        for (const field of line.fields) {
          if (this.isPrincipalActivityCodeField(field.attribName)) {
            field.value = activityCode;
            if (markChanged) {
              field.isChanged = true;
            }
          }
        }
      }
    }
  }

  private updatePrincipalActivityDescFields(activityCode: string, markChanged = false): void {
    const desc = this.getPrincipalBusinessActivityDesc(activityCode);
    for (const section of this.formSections) {
      for (const line of section.lines) {
        for (const field of line.fields) {
          if (this.isPrincipalActivityDescField(field.attribName)) {
            field.value = desc;
            if (markChanged) {
              field.isChanged = true;
            }
          }
        }
      }
    }
  }

  onPrincipalActivityDescChange(activityDesc: string): void {
    this.principalActivityCode = activityDesc;
    const match = this.principalBusinessActivityList.find(
      (a: any) => a.ACTIVITY_DESC === activityDesc
    );
    this.principalBusinessActivityDesc = match?.ACTIVITY_DESC || activityDesc;
    this.cdr.detectChanges();
  }

  private extractMetadataFromRows(rows: any[]): void {
    for (const row of rows) {
      if (String(row.PROPERTY_TYPE) === '*') {
        continue;
      }
      const attrParts = String(row.attrib_value || '').split('~');
      const name = (attrParts[0] || '').toUpperCase();
      const value = this.extractRowValue(row, this.resolveEditType(row, attrParts[0] || ''));

      if (!value) {
        continue;
      }
      const valueStr = String(value).trim();
      if (name.includes('FUNCTIONAL') && name.includes('CURR')) {
        this.functionalCurrency = valueStr;
      } else if (/F8964_ELE_I_B_LINE1H$/i.test(name) || /F8964_ELE_I_B_Line1h$/i.test(attrParts[0] || '')) {
        this.principalActivityCode = valueStr;
      } else if (/F8964_ELE_I_B_LINE1I$/i.test(name) || /F8964_ELE_I_B_Line1i$/i.test(attrParts[0] || '')) {
        this.principalBusinessActivityDesc = valueStr;
      } else if (/F8964_ELE_I_A_LINE1G$|F8964_ELE_I_A_line1g$|F8964_ELE_I_A_LINE2D$|F8964_ELE_I_A_line2d$|F8964_ELE_I_B_LINE1J$|F8964_ELE_I_B_Line1j$/i.test(name)) {
        this.functionalCurrency = valueStr;
      } else if (name.includes('PRINCIPAL') && name.includes('ACTIVITY') && name.includes('CODE')) {
        this.principalActivityCode = valueStr;
      } else if (name.includes('PRINCIPAL') && name.includes('BUSINESS') && !name.includes('CODE')) {
        this.principalBusinessActivityDesc = valueStr;
      }
    }
    this.syncPrincipalBusinessActivityDesc();
  }

  private loadFormData(): void {
    this.formLoadCancel$.next();
    this.loading = true;
    this.errorMessage = '';
    this.formData = null;
    this.formSections = [];
    this.formTitle = '';
    this.cdr.detectChanges();

    const jcdKey = this.resolveJcdKey();
    const params = {
      action_code: '7rc5om',
      irs_form_no: 'F8964',
      schd_name: 'ELE',
      tax_year: this.getTaxYear(),
      scenario: String(
        this.filterParams?.scenario
        ?? this.rowData?.SCENARIO
        ?? this.globalServiceWrapper?.globalParams?.scenario
        ?? ''
      ),
      jcd_key: jcdKey,
      combination_key: String(this.rowData?.COMBINATION_KEY ?? '')
    };

    this.jsonObjectFactory.getJSONObj(
      `${this.GENERAL_CONFIG.base_url}/loadJsonObject`,
      params,
      this.globalServiceWrapper,
      null,
      null
    ).pipe(
      takeUntil(this.formLoadCancel$),
      finalize(() => {
        this.ngZone.run(() => {
          this.loading = false;
          this.cdr.detectChanges();
        });
      })
    ).subscribe(
      (data: any) => {
        this.ngZone.run(() => {
          this.formData = data;

          if (data?.callSuccess != '1') {
            this.errorMessage = data?.errorMessage || 'Error loading data';
          } else {
            try {
              const rows = this.extractRows(data);
              this.extractMetadataFromRows(rows);
              this.processFormData(rows);
              this.refreshFieldDropdownOptions();
              this.syncLinkedLookupFieldValues();
              this.normalizeAllFieldBindings();
              this.originalFormSections = JSON.parse(JSON.stringify(this.formSections));
            } catch (e) {
              console.error('Error processing F8964 ELE form data:', e);
              this.errorMessage = 'Error processing form data.';
            }
          }
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        this.ngZone.run(() => {
          this.errorMessage = 'Failed to load data.';
          console.error('Error fetching F8964 ELE data:', error);
          this.snackbar.openSnackBar('Failed to load data.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        });
      }
    );
  }

  private extractRows(data: any): any[] {
    let payload = data?.jsonObject ?? data;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return [];
      }
    }
    return Array.isArray(payload) ? payload : [];
  }

  processFormData(rows: any[]): void {
    this.formSections = [];
    this.formTitle = '';
    this.rawFormRows = rows;
    this.part4ColumnHeaders = PART4_FIELD_ORDER.map(key => FIELD_SUFFIX_LABELS[key] || key);
    if (!rows.length) {
      return;
    }

    const groups = this.groupByFormLineKey(rows);
    let currentMode: F8964TableMode = 'standard';
    let currentSection: F8964EleSection = { mode: 'standard', lines: [] };

    const flushSection = (): void => {
      if (currentSection.lines.length > 0) {
        this.formSections.push({ ...currentSection, mode: currentMode });
      }
    };

    const startSection = (mode: F8964TableMode): void => {
      flushSection();
      currentMode = mode;
      currentSection = { mode, lines: [] };
    };

    const getCurrentMode = (): F8964TableMode => currentMode;

    for (const group of groups) {
      if (this.isPart3ColumnHeaderGroup(group)) {
        const part3HeaderRow = group.rows.find(r =>
          this.parseAttribValue(r).attribName === 'F8964_ELE_III_PartIII'
        );
        if (part3HeaderRow) {
          currentSection.lines.push({
            kind: 'section-header',
            lineNo: '',
            label: this.resolveRowLabel(part3HeaderRow),
            fields: [],
            formLineKey: group.key
          });
        }
        if (getCurrentMode() !== 'part3') {
          startSection('part3');
        }
        currentSection.lines.push({
          kind: 'subsection-header',
          lineNo: '',
          label: 'Type of Election (see instructions)',
          fields: [],
          formLineKey: group.key
        });
        continue;
      }

      if (this.isPart4ColumnHeaderGroup(group)) {
        if (getCurrentMode() !== 'part4') {
          startSection('part4');
        }
        this.part4ColumnHeaders = this.extractPart4ColumnHeaders(group);
        const line2Row = group.rows.find(r =>
          /^F8964_ELE_IV_Line2$/i.test(this.parseAttribValue(r).attribName)
        );
        if (line2Row) {
          currentSection.lines.push({
            kind: 'subsection-header',
            lineNo: '2',
            label: this.resolveRowLabel(line2Row),
            fields: [],
            formLineKey: group.key
          });
        }
        continue;
      }

      const modeFromAttrib = this.resolveTableModeFromGroup(group);
      if (modeFromAttrib && modeFromAttrib !== getCurrentMode()) {
        startSection(modeFromAttrib);
      }

      const firstRow = group.rows[0];
      const { attribName: firstAttribName } = this.parseAttribValue(firstRow);
      if (this.isStaleBackendGroup(group)) {
        continue;
      }
      const lineTypeCode = this.resolveLineTypeCode(group);
      const derivedLineNo = this.deriveLineNoFromAttrib(firstAttribName);
      const lineNo = derivedLineNo || this.normalizeLineNo(firstRow.line_no);
      const groupDescription = this.getGroupLineDescription(group);
      const headerLabel = groupDescription || this.resolveRowLabel(firstRow);
      const fields = this.buildFields(group.rows);

      if (getCurrentMode() === 'part4' && this.isPart4Line2HeaderGroup(group)) {
        currentSection.lines.push({
          kind: 'subsection-header',
          lineNo: '2',
          label: headerLabel,
          fields: [],
          formLineKey: group.key
        });
        continue;
      }

      if (getCurrentMode() === 'part4' && group.rows.length === 1 && this.isLineDescriptionAttrib(firstAttribName)) {
        const part4LineNo = this.normalizeLineNo(firstRow.line_no);
        if (part4LineNo === '1a' || part4LineNo === '1b') {
          this.upsertPart4Line1Item(
            currentSection,
            part4LineNo,
            fields,
            this.resolvePart4Line1ItemLabel(part4LineNo, headerLabel),
            group.key
          );
          continue;
        }
      }

      if (lineTypeCode === 'FH' || firstAttribName === 'F8964_ELE_HEADER') {
        this.formTitle = headerLabel || this.formTitle;
        currentSection.lines.push({
          kind: 'form-header',
          lineNo: '',
          label: headerLabel,
          fields: [],
          formLineKey: group.key
        });
        continue;
      }

      if (this.isSectionHeaderGroup(group)) {
        const isPartIiHeader = firstAttribName === 'F8964_ELE_II_PartII';
        const sectionLabel = this.formatPartSectionLabel(firstRow, groupDescription || headerLabel);
        const isPartHeader = /^(Part |Schedule )/i.test(sectionLabel) || this.isPartLineNo(firstRow.line_no);
        currentSection.lines.push({
          kind: isPartHeader ? 'section-header' : 'subsection-header',
          lineNo: lineNo,
          label: sectionLabel,
          fields: [],
          formLineKey: group.key
        });
        if (isPartIiHeader) {
          this.appendInstructionNote(PART_II_INTRO_NOTE, currentSection, group.key);
        }
        continue;
      }

      if (/^F8964_ELE_IV_Line1a$/i.test(firstAttribName) && fields.length > 0) {
        this.upsertPart4Line1Item(currentSection, '1a', fields, '', group.key);
        continue;
      }

      if (/^F8964_ELE_IV_Line1b$/i.test(firstAttribName) && fields.length > 0) {
        this.upsertPart4Line1Item(currentSection, '1b', fields, '', group.key);
        continue;
      }

      if (getCurrentMode() === 'part3' && this.isPart3ElectionGroup(group)) {
        const baseRow = group.rows.find(r =>
          /F8964_ELE_III_NA_LINE\d+$/i.test(this.parseAttribValue(r).attribName)
        );
        const baseAttribName = baseRow
          ? this.parseAttribValue(baseRow).attribName
          : firstAttribName;
        const electionLineNo = this.extractPart3LineNo(fields, baseAttribName);
        currentSection.lines.push({
          kind: 'multi-col',
          lineNo: electionLineNo,
          label: groupDescription || this.getPart3ElectionLabel(fields, baseAttribName),
          fields: this.sortPart3Fields(fields),
          formLineKey: group.key
        });
        continue;
      }

      if (getCurrentMode() === 'part4' && this.isPart4ShareholderGroup(group)) {
        currentSection.lines.push({
          kind: 'multi-col',
          lineNo: lineNo,
          label: '',
          fields: this.sortPart4Fields(fields),
          formLineKey: group.key
        });
        continue;
      }

      if (group.rows.length === 1 && this.isLineDescriptionAttrib(firstAttribName) && fields.length === 0) {
        currentSection.lines.push({
          kind: 'data',
          lineNo: lineNo,
          label: headerLabel,
          fields: [],
          formLineKey: group.key
        });
        continue;
      }

      if (String(firstRow.PROPERTY_TYPE) === '2' && fields.length === 0) {
        currentSection.lines.push({
          kind: 'data',
          lineNo: lineNo,
          label: headerLabel,
          fields: [],
          formLineKey: group.key
        });
        continue;
      }

      if (fields.length === 0) {
        continue;
      }

      currentSection.lines.push({
        kind: fields.length > 1 ? 'multi-col' : 'data',
        lineNo: lineNo,
        label: headerLabel,
        fields,
        formLineKey: group.key
      });
      const noteKey = fields.find(f => /AUTH_OWN_[YN]$/i.test(f.attribName))?.attribName || firstAttribName;
      this.appendInstructionNotesForAttrib(noteKey, currentSection, group.key);
    }

    flushSection();

    this.modalTitle = 'Section 987 Elections';

    this.consolidatePart4Line1Sections();
    this.ensurePart4Line1Fields();
    this.part4HiddenRowIndices.clear();
    this.part4DeletedRowIndices.clear();
    this.ensurePart4DefaultShareholderEntry();
    this.initializeAllAddressModes();
    this.originalFormSections = JSON.parse(JSON.stringify(this.formSections));
  }

  getColSpan(mode: F8964TableMode): number {
    if (mode === 'part3') {
      return 5;
    }
    if (mode === 'part4') {
      return 7;
    }
    return 3;
  }

  getFieldBySuffix(fields: F8964EleField[], suffix: string): F8964EleField | null {
    const aliases = PART3_FIELD_SUFFIX_ALIASES[suffix] || [suffix];
    for (const alias of aliases) {
      const match = fields.find(f =>
        !this.isSecondaryPairedField(fields, f) && this.matchesFieldAlias(f.attribName, alias)
      );
      if (match) {
        return match;
      }
    }
    return fields.find(f =>
      !this.isSecondaryPairedField(fields, f)
      && f.attribName.replace(/\s/g, '').includes(suffix.replace(/\s/g, ''))
    ) || null;
  }

  /**
   * API often sends paired _NAME_1/_NAME_2 (and similar) for one logical PDF field.
   * Hide the secondary field in the UI; it remains on the line for save.
   */
  trackByField(_index: number, field: F8964EleField): string {
    return `${field.lineAttribKey || ''}_${field.attribName}_${field.objectId || ''}`;
  }

  getDisplayFields(fields: F8964EleField[]): F8964EleField[] {
    if (!fields?.length) {
      return [];
    }
    const useForeignAddress = this.hasDualAddressBlock(fields) && this.shouldShowForeignAddress(fields);
    const filtered = fields.filter(field => {
      if (this.isSecondaryPairedField(fields, field)) {
        return false;
      }
      if (!this.hasDualAddressBlock(fields)) {
        return true;
      }
      if (this.isForeignAddressAttrib(field.attribName)) {
        if (this.isCountryField(field.attribName)) {
          return true;
        }
        return useForeignAddress;
      }
      if (this.isUsAddressAttrib(field.attribName)) {
        return !useForeignAddress;
      }
      return true;
    });
    return this.sortAddressDisplayFields(filtered, useForeignAddress);
  }

  isAddressField(field: F8964EleField, lineFields: F8964EleField[] = []): boolean {
    if (this.isUsAddressAttrib(field.attribName) || this.isForeignAddressAttrib(field.attribName)) {
      return true;
    }
    return this.isNameLineField(field) && this.lineHasAddressFields(lineFields);
  }

  shouldShowAddressFieldLabel(field: F8964EleField, lineFields: F8964EleField[] = []): boolean {
    return this.isAddressField(field, lineFields);
  }

  private lineHasAddressFields(lineFields: F8964EleField[]): boolean {
    return (lineFields || []).some(f =>
      this.isUsAddressAttrib(f.attribName) || this.isForeignAddressAttrib(f.attribName)
    );
  }

  isNameLineField(field: F8964EleField): boolean {
    return /_NAME_1$|_USSH_NAME_1$/i.test(field.attribName);
  }

  /** US state/province — dropdown when country is US (form-8858-textualinfo pattern). */
  isUsStateField(field: F8964EleField): boolean {
    return this.isUsAddressAttrib(field.attribName)
      && /_STATE$/i.test(field.attribName)
      && !/_F_STATE$/i.test(field.attribName);
  }

  /** Foreign state/province — free-text when country is not US (form-8858-textualinfo pattern). */
  isForeignStateField(field: F8964EleField): boolean {
    return /_F_STATE$/i.test(field.attribName);
  }

  isUsStateDropdown(field: F8964EleField, lineFields: F8964EleField[]): boolean {
    if (!this.isUsStateField(field)) {
      return false;
    }
    const context = lineFields?.length ? lineFields : [];
    if (this.hasDualAddressBlock(context)) {
      return !this.shouldShowForeignAddress(context);
    }
    return this.isUsCountrySelected(context);
  }

  isUsStateTextInput(field: F8964EleField, lineFields: F8964EleField[] = []): boolean {
    if (!this.isUsStateField(field) || this.isUsStateDropdown(field, lineFields)) {
      return false;
    }
    const context = lineFields?.length ? lineFields : [];
    return !(this.hasDualAddressBlock(context) && this.shouldShowForeignAddress(context));
  }

  private getAddressCountryValue(fields: F8964EleField[]): string {
    const countryField = fields.find(f => this.isCountryField(f.attribName));
    return String(countryField?.value || '').trim().toUpperCase();
  }

  private isUsCountrySelected(fields: F8964EleField[]): boolean {
    const country = this.getAddressCountryValue(fields);
    return country === 'US' || country === 'USA';
  }

  getAddressFieldLabel(field: F8964EleField, lineFields: F8964EleField[] = []): string {
    const name = field.attribName.toUpperCase();
    if (/_NAME_1$|_USSH_NAME_1$/i.test(name)) {
      return 'Name line 1';
    }
    if (/_NAME_2$|_USSH_NAME_2$/i.test(name)) {
      return 'Name line 2';
    }
    if (this.isCountryField(field.attribName)) {
      return 'Country';
    }
    if (/_ADDR_1$/i.test(name) && !/_F_ADDR_1$/i.test(name)) {
      return 'Line 1';
    }
    if (/_F_ADDR_1$/i.test(name)) {
      return 'Line 1';
    }
    if (/_ADDR_2$/i.test(name) && !/_F_ADDR_2$/i.test(name)) {
      const hasLine1 = lineFields.some(f =>
        /_ADDR_1$/i.test(f.attribName) && !/_F_ADDR_/i.test(f.attribName)
      );
      return hasLine1 ? 'Line 2' : 'Line 1';
    }
    if (/_F_ADDR_2$/i.test(name)) {
      const hasLine1 = lineFields.some(f => /_F_ADDR_1$/i.test(f.attribName));
      return hasLine1 ? 'Line 2' : 'Line 1';
    }
    if (/_CITY$|_F_CITY$/i.test(name)) {
      return 'City';
    }
    if (/_STATE$|_F_STATE$/i.test(name)) {
      return 'State';
    }
    if (/_ZIP$|_F_ZIP$/i.test(name)) {
      return 'Zip/Postal Code';
    }
    return field.fieldLabel;
  }

  private hasDualAddressBlock(fields: F8964EleField[]): boolean {
    return fields.some(f => this.isUsAddressAttrib(f.attribName))
      && fields.some(f => this.isForeignAddressAttrib(f.attribName));
  }

  private shouldShowForeignAddress(fields: F8964EleField[]): boolean {
    return this.foreignAddressLineKeys.has(this.resolveAddressLineKey(fields));
  }

  private resolveAddressLineKey(fields: F8964EleField[]): string {
    const fromField = fields.find(f => f.lineAttribKey)?.lineAttribKey || fields[0]?.lineAttribKey;
    if (fromField) {
      return String(fromField);
    }
    const sample = fields[0]?.attribName || '';
    const match = sample.match(/^(F8964_ELE_[A-Z0-9_]+?)(?:_(?:NAME|ADDR|F_|CITY|STATE|ZIP))/i);
    return match?.[1] || sample;
  }

  private initializeAllAddressModes(): void {
    this.foreignAddressLineKeys.clear();
    for (const section of this.formSections) {
      for (const line of section.lines) {
        if (line.fields?.length && this.hasDualAddressBlock(line.fields)) {
          this.initializeAddressModeForLine(line.fields);
        }
      }
    }
  }

  private isCountryField(attribName: string): boolean {
    return /_(?:F_|US_)?COUNTRY$/i.test(attribName);
  }

  private initializeAddressModeForLine(fields: F8964EleField[]): void {
    const lineKey = this.resolveAddressLineKey(fields);
    const countryField = fields.find(f => this.isCountryField(f.attribName));
    const country = String(countryField?.value || '').trim().toUpperCase();

    if (country === 'US' || country === 'USA') {
      this.foreignAddressLineKeys.delete(lineKey);
      return;
    }
    if (country) {
      this.foreignAddressLineKeys.add(lineKey);
      return;
    }

    const foreignHasData = fields.some(f =>
      this.isForeignAddressAttrib(f.attribName)
      && !this.isCountryField(f.attribName)
      && String(f.value || '').trim()
    );
    const usHasData = fields.some(f =>
      this.isUsAddressAttrib(f.attribName) && String(f.value || '').trim()
    );
    if (foreignHasData && !usHasData) {
      this.foreignAddressLineKeys.add(lineKey);
    } else {
      this.foreignAddressLineKeys.delete(lineKey);
    }
  }

  private updateAddressModeFromCountry(fields: F8964EleField[], countryValue: string): void {
    const lineKey = this.resolveAddressLineKey(fields);
    const country = String(countryValue || '').trim().toUpperCase();
    if (country && country !== 'US' && country !== 'USA') {
      this.foreignAddressLineKeys.add(lineKey);
    } else {
      this.foreignAddressLineKeys.delete(lineKey);
    }
  }

  private sortAddressDisplayFields(fields: F8964EleField[], useForeignAddress: boolean): F8964EleField[] {
    /**
     * Fixed 8858 address order (does not change when country toggles US/foreign):
     * Name line 1 → Country → Line 1 → Line 2 → City → State → Zip
     */
    const rank = (field: F8964EleField): number => {
      const name = field.attribName.toUpperCase();
      if (/_NAME_1$|_USSH_NAME_1$/i.test(name)) {
        return 10;
      }
      if (this.isCountryField(field.attribName)) {
        return 20;
      }
      if (/_NAME_2$|_USSH_NAME_2$/i.test(name)) {
        return 15;
      }
      if (!useForeignAddress) {
        if (/_ADDR_1$/i.test(name) && !/_F_ADDR_/i.test(name)) {
          return 30;
        }
        if (/_ADDR_2$/i.test(name) && !/_F_ADDR_/i.test(name)) {
          return 40;
        }
        if (/_CITY$/i.test(name) && !/_F_CITY$/i.test(name)) {
          return 50;
        }
        if (/_STATE$/i.test(name) && !/_F_STATE$/i.test(name)) {
          return 60;
        }
        if (/_ZIP$/i.test(name) && !/_F_ZIP$/i.test(name)) {
          return 70;
        }
      } else {
        if (/_F_ADDR_1$/i.test(name)) {
          return 30;
        }
        if (/_F_ADDR_2$/i.test(name)) {
          return 40;
        }
        if (/_F_CITY$/i.test(name)) {
          return 50;
        }
        if (/_F_STATE$/i.test(name)) {
          return 60;
        }
        if (/_F_ZIP$/i.test(name)) {
          return 70;
        }
      }
      return 100;
    };

    return [...fields].sort((a, b) => {
      const diff = rank(a) - rank(b);
      if (diff !== 0) {
        return diff;
      }
      return (a.rowOrder || 0) - (b.rowOrder || 0);
    });
  }

  private isForeignAddressAttrib(attribName: string): boolean {
    return /_F_(ADDR_[12]|CITY|STATE|COUNTRY|ZIP)$/i.test(attribName);
  }

  private isUsAddressAttrib(attribName: string): boolean {
    if (this.isForeignAddressAttrib(attribName)) {
      return false;
    }
    return /_(ADDR_[12]|CITY|STATE|ZIP)$/i.test(attribName);
  }

  getPart3LabelFields(fields: F8964EleField[]): F8964EleField[] {
    return fields.filter(field => field.editType === 'checkbox');
  }

  getPairedRadioField(allFields: F8964EleField[], field: F8964EleField): F8964EleField | null {
    if (/_Y$/i.test(field.attribName)) {
      const nName = field.attribName.replace(/_Y$/i, '_N');
      return allFields.find(f => f.attribName.toUpperCase() === nName.toUpperCase()) || null;
    }
    if (/_N$/i.test(field.attribName)) {
      const yName = field.attribName.replace(/_N$/i, '_Y');
      return allFields.find(f => f.attribName.toUpperCase() === yName.toUpperCase()) || null;
    }
    return null;
  }

  isRadioSelected(field: F8964EleField, allFields: F8964EleField[], value: 'Y' | 'N'): boolean {
    return this.getRadioGroupValue(field, allFields) === value;
  }

  getPart3ColumnFields(fields: F8964EleField[], colKey: string): F8964EleField[] {
    const field = this.getFieldBySuffix(fields, colKey);
    return field ? [field] : [];
  }

  private isSecondaryPairedField(allFields: F8964EleField[], field: F8964EleField): boolean {
    const upper = field.attribName.toUpperCase();
    const pairRules: Array<{ secondary: RegExp; toPrimary: (name: string) => string; radioOnly?: boolean }> = [
      { secondary: /_EIN_MISSING_REASON$/i, toPrimary: name => name.replace(/_EIN_MISSING_REASON$/i, '_EIN') },
      { secondary: /_USSH_NAME_2$/i, toPrimary: name => name.replace(/_USSH_NAME_2$/i, '_USSH_NAME_1') },
      { secondary: /_F_ADDR_2$/i, toPrimary: name => name.replace(/_F_ADDR_2$/i, '_F_ADDR_1') },
      { secondary: /_ADDR_2$/i, toPrimary: name => name.replace(/_ADDR_2$/i, '_ADDR_1') },
      { secondary: /_NAME_2$/i, toPrimary: name => name.replace(/_NAME_2$/i, '_NAME_1') },
      { secondary: /_N$/, toPrimary: name => name.replace(/_N$/i, '_Y'), radioOnly: true }
    ];
    for (const rule of pairRules) {
      if (rule.secondary.test(upper)) {
        const primaryName = rule.toPrimary(field.attribName).toUpperCase();
        const primaryField = allFields.find(f => f.attribName.toUpperCase() === primaryName);
        if (primaryField && (!rule.radioOnly || (field.editType === 'radio' && primaryField.editType === 'radio'))) {
          return true;
        }
      }
    }
    return false;
  }

  private matchesFieldAlias(attribName: string, alias: string): boolean {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();
    const aliasNorm = alias.replace(/\s/g, '').toUpperCase();
    switch (aliasNorm) {
      case 'EIN':
        return /_EIN$/i.test(attribName);
      case 'IDENT_NUM':
        return /IDENT_NUM/i.test(normalized);
      case 'EFFECTIVE_DATE_ELE':
      case 'EFFEC_ELE':
        return /EFFECTIVE_DATE_ELE/i.test(normalized) || /EFFEC_ELE/i.test(normalized);
      case 'EFFECTIVE_DATE_REV':
      case 'EFFEC_REV':
        return /EFFECTIVE_DATE_REV/i.test(normalized) || /EFFEC_REV/i.test(normalized);
      case 'INPUT_CHE':
        return /INPUT_CHE/i.test(normalized);
      default:
        return normalized.includes(aliasNorm);
    }
  }

  isPart4ShareholderHeaderLine(line: F8964EleRenderLine): boolean {
    return line.kind === 'subsection-header'
      && /Controlling Domestic Shareholders and EIN/i.test(line.label || '');
  }

  isPart4ShareholderLine(line: F8964EleRenderLine): boolean {
    return line.kind === 'multi-col'
      && line.fields.some(field => /F8964_ELE_IV_LINE2[A-E]_/i.test(field.attribName));
  }

  isPart4RowVisible(line: F8964EleRenderLine): boolean {
    const rowIndex = this.getPart4ShareholderLines().indexOf(line);
    return rowIndex > -1 && this.getPart4VisibleRowIndices().includes(rowIndex);
  }

  getPart4DisplayRowNumber(line: F8964EleRenderLine): number {
    const rowIndex = this.getPart4ShareholderLines().indexOf(line);
    const visiblePos = this.getPart4VisibleRowIndices().indexOf(rowIndex);
    return visiblePos + 1;
  }

  private getPart4ActiveRowIndices(): number[] {
    const rows = this.getPart4ShareholderLines();
    return rows.map((_, index) => index).filter(index => !this.part4HiddenRowIndices.has(index));
  }

  private getPart4VisibleRowIndices(): number[] {
    const active = this.getPart4ActiveRowIndices();
    const visibleCount = Math.max(1, Math.min(this.part4VisibleRowCount, active.length));
    return active.slice(0, visibleCount);
  }

  addPart4ShareholderRow(): void {
    const active = this.getPart4ActiveRowIndices();
    const visible = this.getPart4VisibleRowIndices();

    if (visible.length < active.length) {
      this.part4VisibleRowCount += 1;
      this.cdr.detectChanges();
      return;
    }

    const hiddenSorted = [...this.part4HiddenRowIndices].sort((a, b) => a - b);
    if (hiddenSorted.length) {
      const restoredIndex = hiddenSorted[0];
      this.part4HiddenRowIndices.delete(restoredIndex);
      this.part4DeletedRowIndices.delete(restoredIndex);
      this.part4VisibleRowCount += 1;
      this.cdr.detectChanges();
      return;
    }

    this.snackbar.openSnackBar('No additional rows available.', 'gtw-snackbar--yellow');
  }

  deletePart4ShareholderRow(line: F8964EleRenderLine, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    const rows = this.getPart4ShareholderLines();
    const rowIndex = rows.indexOf(line);
    if (rowIndex === -1) {
      return;
    }

    const visible = this.getPart4VisibleRowIndices();
    if (visible.length <= 1 || !visible.includes(rowIndex)) {
      return;
    }

    if (this.part4ShareholderRowHasPersistedData(line)) {
      this.part4DeletedRowIndices.add(rowIndex);
    } else {
      this.part4DeletedRowIndices.delete(rowIndex);
    }

    line.fields.forEach(field => {
      field.value = '';
      field.isChanged = false;
    });

    this.part4HiddenRowIndices.add(rowIndex);
    if (this.part4VisibleRowCount > 1) {
      this.part4VisibleRowCount -= 1;
    }
    this.cdr.detectChanges();
  }

  isCheckboxField(field: F8964EleField): boolean {
    return field.editType === 'checkbox';
  }

  isFieldDisabled(field: F8964EleField, lineFields: F8964EleField[] = []): boolean {
    if (field.isEditableFlag === 'N') {
      return true;
    }
    if (lineFields.length && this.isPart3ElectionDetailField(field)) {
      return !this.isPart3ElectionMade(lineFields);
    }
    return false;
  }

  isPart3ElectionCheckboxField(field: F8964EleField): boolean {
    return (PART3_FIELD_SUFFIX_ALIASES.Input_che || ['Input_che']).some(alias =>
      this.matchesFieldAlias(field.attribName, alias)
    );
  }

  isPart3ElectionDetailField(field: F8964EleField): boolean {
    for (const colKey of ['Ident_num', 'Effec_ele', 'Effec_rev']) {
      const aliases = PART3_FIELD_SUFFIX_ALIASES[colKey] || [colKey];
      if (aliases.some(alias => this.matchesFieldAlias(field.attribName, alias))) {
        return true;
      }
    }
    return false;
  }

  isPart3ElectionMade(lineFields: F8964EleField[]): boolean {
    const checkbox = lineFields.find(f =>
      f.editType === 'checkbox' && this.isPart3ElectionCheckboxField(f)
    );
    return !!checkbox && this.isCheckboxChecked(checkbox);
  }

  getDateFieldModel(field: F8964EleField): string {
    return toIsoDateString(field.value);
  }

  onDateFieldChange(field: F8964EleField, value: string): void {
    field.value = value || '';
    this.onFieldChange(field);
  }

  /** Block keyboard/paste entry — date may only be set via the native date picker. */
  onDateFieldKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      return;
    }
    event.preventDefault();
  }

  onDateFieldPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  onDateFieldDrop(event: DragEvent): void {
    event.preventDefault();
  }

  onDateFieldBeforeInput(event: Event): void {
    event.preventDefault();
  }

  isRightAlignedField(field: F8964EleField): boolean {
    return field.editType === 'number'
      || /Perc|Amt|Num|Percent/i.test(field.attribName);
  }

  isCheckboxChecked(field: F8964EleField): boolean {
    const val = String(field.value ?? '').toLowerCase();
    return val === '1' || val === 'true' || val === 'y';
  }

  getDropdownPlaceholder(field: F8964EleField): string {
    switch (this.resolveLookupType(field.attribName)) {
      case 'country':
        return 'Select Country';
      case 'currency':
        return 'Select Functional Currency';
      case 'principal-activity-code':
        return 'Select Principal Business Activity Code';
      case 'state':
        return 'Select State';
      default:
        return 'Select';
    }
  }

  readonly part3ColumnKeys = PART3_FIELD_ORDER;
  readonly part4ColumnKeys = PART4_FIELD_ORDER;

  getPart4ColumnHeader(colKey: string): string {
    const index = PART4_FIELD_ORDER.indexOf(colKey);
    if (index >= 0 && this.part4ColumnHeaders[index]) {
      return this.part4ColumnHeaders[index];
    }
    return FIELD_SUFFIX_LABELS[colKey] || colKey;
  }

  isPartLabelLine(line: F8964EleRenderLine): boolean {
    return line.kind === 'section-header' && /^Part\s/i.test(line.label || '');
  }

  getPartLabelHeaders(section: F8964EleSection): F8964EleRenderLine[] {
    return section.lines.filter(line => line.kind === 'section-header' && this.isPartLabelLine(line));
  }

  shouldRenderPartLabelAboveTable(section: F8964EleSection, line: F8964EleRenderLine): boolean {
    return line.kind === 'section-header'
      && this.isPartLabelLine(line)
      && (section.mode === 'part3' || section.mode === 'part4');
  }

  getPart4Line1Items(section: F8964EleSection): F8964EleRenderLine[] {
    return section.lines.filter(line => line.kind === 'part4-line1-item');
  }

  getVisiblePart4ShareholderTableLines(section: F8964EleSection): F8964EleRenderLine[] {
    return this.getPart4ShareholderTableLines(section).filter(line => this.isPart4RowVisible(line));
  }

  canDeletePart4ShareholderRow(line: F8964EleRenderLine): boolean {
    const rowIndex = this.getPart4ShareholderLines().indexOf(line);
    if (rowIndex === -1) {
      return false;
    }
    const visible = this.getPart4VisibleRowIndices();
    return visible.length > 1 && visible.includes(rowIndex);
  }

  getPart4Line2Header(section: F8964EleSection): F8964EleRenderLine | undefined {
    return section.lines.find(line => this.isPart4ShareholderHeaderLine(line));
  }

  getPart4ShareholderTableLines(section: F8964EleSection): F8964EleRenderLine[] {
    return section.lines.filter(line =>
      line.kind === 'multi-col' && this.isPart4ShareholderLine(line)
    );
  }

  shouldRenderLineInStandardTable(section: F8964EleSection, line: F8964EleRenderLine): boolean {
    if (this.shouldRenderPartLabelAboveTable(section, line)) {
      return false;
    }
    if (section.mode === 'part4') {
      return line.kind !== 'section-header'
        && line.kind !== 'part4-line1-item'
        && line.kind !== 'part4-line1'
        && line.kind !== 'subsection-header'
        && !(line.kind === 'data' && (line.lineNo === '1a' || line.lineNo === '1b'));
    }
    return true;
  }

  getPart4FieldsForColumn(fields: F8964EleField[], colKey: string): F8964EleField[] {
    const matched = fields
      .filter(field => this.fieldMatchesPart4Column(field.attribName, colKey))
      .sort((a, b) => {
        const ai = PART4_FIELD_SORT_ORDER.findIndex(k => a.attribName.toUpperCase().includes(k.toUpperCase()));
        const bi = PART4_FIELD_SORT_ORDER.findIndex(k => b.attribName.toUpperCase().includes(k.toUpperCase()));
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    return this.getDisplayFields(matched);
  }

  private fieldMatchesPart4Column(attribName: string, colKey: string): boolean {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();
    switch (colKey) {
      case 'Name_CFC':
        return /_NAME_[12]$/i.test(attribName) && !/_USSH_NAME_/i.test(attribName)
          || normalized.includes('NAME_CFC');
      case 'Iden_Num':
        return /_EIN$/i.test(attribName) && !/_EIN_MISSING_REASON$/i.test(attribName)
          || /IDEN_NUM/i.test(normalized);
      case 'Refe_ID':
        return /_REF_ID$/i.test(attribName) || normalized.includes('REFE_ID');
      case 'Name_Sha':
        return /_USSH_NAME_[12]$/i.test(attribName) || normalized.includes('NAME_SHA');
      case 'TIN_Sha':
        return /_USSH_TIN$/i.test(attribName) || normalized.includes('TIN_SHA');
      case 'Perc_Sto':
        return /_VOTING_STOCK$/i.test(attribName) || normalized.includes('PERC_STO');
      default: {
        const aliases = PART4_COLUMN_SUFFIX_ALIASES[colKey] || [colKey];
        return aliases.some(alias => normalized.includes(alias.replace(/\s/g, '').toUpperCase()));
      }
    }
  }

  showPart4FieldSubLabel(_columnFields: F8964EleField[], _field: F8964EleField): boolean {
    return false;
  }

  private formatPartSectionLabel(row: any, description: string): string {
    const { attribName } = this.parseAttribValue(row);
    if (SECTION_LABELS[attribName]) {
      return SECTION_LABELS[attribName];
    }

    const rawLineNo = String(row?.line_no || '').trim();
    if (/^Part\s*-/i.test(rawLineNo)) {
      const partLabel = rawLineNo.replace(/\s*-\s*/g, ' ').trim();
      if (description && !/^Part\s/i.test(description)) {
        return `${partLabel} — ${description}`;
      }
      return partLabel || description;
    }

    if (/^Section\s+/i.test(rawLineNo)) {
      const sectionPrefix = rawLineNo.replace(/\s+/g, ' ').trim();
      const trimmedDesc = (description || '').trim();
      if (trimmedDesc && !new RegExp(`^${sectionPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(trimmedDesc)) {
        return `${sectionPrefix} — ${trimmedDesc}`;
      }
      return sectionPrefix || trimmedDesc;
    }

    return description;
  }

  private parseAttribValue(row: any): { attribName: string; lineAttribKey: string; description: string } {
    const attrParts = String(row?.attrib_value || '').split('~');
    return {
      attribName: (attrParts[0] || '').trim(),
      lineAttribKey: attrParts[1] || '',
      description: (attrParts[3] || '').trim()
    };
  }

  private isLineDescriptionAttrib(attribName: string): boolean {
    return /^LINE_DESCRIPTION$/i.test(attribName);
  }

  private getLineDescriptionRow(group: { rows: any[] }): any | null {
    return group.rows.find(row => this.isLineDescriptionAttrib(this.parseAttribValue(row).attribName)) || null;
  }

  private getGroupLineDescription(group: { rows: any[] }): string {
    const lineDescRow = this.getLineDescriptionRow(group);
    return lineDescRow ? this.resolveRowLabel(lineDescRow) : '';
  }

  private isPartLineNo(lineNo: any): boolean {
    return /^Part\s*-/i.test(String(lineNo || '').trim());
  }

  private isSectionSubheadingLineNo(lineNo: any): boolean {
    return /^Section\s+/i.test(String(lineNo || '').trim());
  }

  private isPartOrSectionLineNo(lineNo: any): boolean {
    return this.isPartLineNo(lineNo) || this.isSectionSubheadingLineNo(lineNo);
  }

  private resolveTableModeFromGroup(group: { rows: any[] }): F8964TableMode | null {
    const names = group.rows.map(r => this.parseAttribValue(r).attribName).join(' ');
    const lineNos = group.rows.map(r => String(r.line_no || '')).join(' ');
    if (/F8964_ELE_III_NA_LINE\d+/i.test(names)) {
      return 'part3';
    }
    if (/F8964_ELE_IV_/i.test(names)) {
      return 'part4';
    }
    if (/Part\s*-\s*III/i.test(lineNos)) {
      return 'part3';
    }
    if (/Part\s*-\s*IV/i.test(lineNos)) {
      return 'part4';
    }
    return null;
  }

  private extractPart4ColumnHeaders(group: { rows: any[] }): string[] {
    return PART4_FIELD_ORDER.map(key => {
      const headerRow = group.rows.find(row => {
        const { attribName } = this.parseAttribValue(row);
        return new RegExp(`IV_Line2_${key}$`, 'i').test(attribName);
      });
      if (headerRow) {
        const label = this.resolveRowLabel(headerRow);
        if (label) {
          return label;
        }
      }
      return FIELD_SUFFIX_LABELS[key] || key;
    });
  }

  private isPart4ColumnHeaderGroup(group: { rows: any[] }): boolean {
    if (!group.rows.length) {
      return false;
    }
    const names = group.rows.map(r => this.parseAttribValue(r).attribName);
    const hasLine2Description = names.some(n => /^F8964_ELE_IV_Line2$/i.test(n));
    const hasColumnPlaceholders = names.some(n =>
      /IV_Line2_(Name_CFC|Iden_Num|Refe_ID|Name_Sha|TIN_Sha|Perc_Sto)$/i.test(n)
    );
    return hasLine2Description && hasColumnPlaceholders;
  }

  private isPart3ColumnHeaderGroup(group: { rows: any[] }): boolean {
    if (!group.rows.length) {
      return false;
    }
    const names = group.rows.map(r => this.parseAttribValue(r).attribName);
    const hasPart3Header = names.some(n => n === 'F8964_ELE_III_PartIII');
    const hasElectionRow = names.some(n => /III_NA_Line\d+/i.test(n));
    const hasColumnPlaceholders = names.some(n =>
      /^F8964_ELE_III_(Ident_num|Effec_|Input_che)/i.test(n)
    );
    return hasPart3Header && hasColumnPlaceholders && !hasElectionRow;
  }

  private deriveLineNoFromAttrib(attribName: string): string {
    if (Object.prototype.hasOwnProperty.call(PDF_LINE_NO_OVERRIDES, attribName)) {
      return PDF_LINE_NO_OVERRIDES[attribName];
    }

    let     match = attribName.match(/III_NA_LINE(\d+)/i);
    if (match) {
      return match[1];
    }
    match = attribName.match(/IV_LINE2([A-E])/i);
    if (match) {
      return `2${match[1].toLowerCase()}`;
    }
    match = attribName.match(/IV_Line2([a-e])/i);
    if (match) {
      return `2${match[1]}`;
    }
    match = attribName.match(/IV_Line1([a-b])/i);
    if (match) {
      return `1${match[1]}`;
    }
    match = attribName.match(/I_A_line([\da-z]+(?:\([^)]+\))*)/i);
    if (match) {
      return match[1];
    }
    match = attribName.match(/I_B_Line([\da-z]+(?:\([^)]+\))*)/i);
    if (match) {
      return match[1];
    }
    match = attribName.match(/II_A_Line(\d+[a-z]?)/i);
    if (match) {
      return match[1];
    }
    match = attribName.match(/II_B_Line(\d+)/i);
    if (match) {
      return match[1];
    }
    return '';
  }

  private isPart4Line2HeaderGroup(group: { rows: any[] }): boolean {
    if (group.rows.length !== 1) {
      return false;
    }
    const row = group.rows[0];
    const { attribName } = this.parseAttribValue(row);
    if (!this.isLineDescriptionAttrib(attribName)) {
      return /^F8964_ELE_IV_Line2$/i.test(attribName);
    }
    const label = this.resolveRowLabel(row);
    return /Controlling Domestic Shareholders and EIN/i.test(label);
  }

  private isLabelOnlyAttrib(attribName: string): boolean {
    if (this.isLineDescriptionAttrib(attribName)) {
      return true;
    }
    if (/^F8964_ELE_III_NA_LINE\d+$/i.test(attribName)) {
      return false;
    }
    if (/^F8964_ELE_III_NA_Line\d+$/i.test(attribName)) {
      return true;
    }
    if (/^F8964_ELE_IV_Line2[a-e]?$/i.test(attribName)) {
      return true;
    }
    if (/^F8964_ELE_IV_LINE2[A-E]$/i.test(attribName)) {
      return true;
    }
    return false;
  }

  private isStaleBackendGroup(group: { rows: any[] }): boolean {
    return group.rows.every(r => STALE_BACKEND_ROWS.has(this.parseAttribValue(r).attribName));
  }

  private resolveTableMode(headerLabel: string): F8964TableMode {
    if (/Part\s+III/i.test(headerLabel)) {
      return 'part3';
    }
    if (/Part\s+IV/i.test(headerLabel)) {
      return 'part4';
    }
    return 'standard';
  }

  private resolveLineTypeCode(group: { rows: any[] }): string {
    const firstRow = group.rows[0];
    const code = String(firstRow.object_id || '').split('~')[5]?.toUpperCase() || '';
    if (group.rows.length > 1 && group.rows.some(r => this.isEditableField(r))) {
      return 'DT';
    }
    return code;
  }

  private isSectionHeaderGroup(group: { rows: any[] }): boolean {
    if (group.rows.length !== 1) {
      return false;
    }
    const row = group.rows[0];
    const { attribName, description } = this.parseAttribValue(row);
    if (this.isLineDescriptionAttrib(attribName)) {
      if (this.isPartOrSectionLineNo(row.line_no)) {
        return true;
      }
      if (description && /^(Part |Schedule |Section )/i.test(description)) {
        return true;
      }
      return false;
    }
    if (description && /^(Part |Schedule |Section )/i.test(description)) {
      return true;
    }
    if (SECTION_LABELS[attribName]) {
      return true;
    }
    return false;
  }

  private isPart3ElectionGroup(group: { rows: any[] }): boolean {
    return group.rows.some(r => {
      const name = String(r.attrib_value || '').split('~')[0] || '';
      return /III_NA_LINE\d+/i.test(name);
    });
  }

  private isPart4ShareholderGroup(group: { rows: any[] }): boolean {
    return group.rows.some(r => {
      const name = String(r.attrib_value || '').split('~')[0] || '';
      return /F8964_ELE_IV_LINE2[A-E]_/i.test(name);
    });
  }

  private getPart4ShareholderLines(): F8964EleRenderLine[] {
    const rows: F8964EleRenderLine[] = [];
    for (const section of this.formSections) {
      if (section.mode !== 'part4') {
        continue;
      }
      rows.push(...section.lines.filter(line => this.isPart4ShareholderLine(line)));
    }
    return rows;
  }

  private ensurePart4Line1Fields(): void {
    const part4Section = this.formSections.find(section => section.mode === 'part4');
    if (!part4Section) {
      return;
    }

    for (const lineNo of ['1a', '1b']) {
      let line = part4Section.lines.find(
        item => item.kind === 'part4-line1-item' && String(item.lineNo).toLowerCase() === lineNo
      );
      if (!line) {
        line = {
          kind: 'part4-line1-item',
          lineNo,
          label: this.resolvePart4Line1ItemLabel(lineNo, ''),
          fields: [],
          formLineKey: `part4-line1-${lineNo}`
        };
        const line2HeaderIndex = part4Section.lines.findIndex(item => this.isPart4ShareholderHeaderLine(item));
        const insertIndex = line2HeaderIndex >= 0 ? line2HeaderIndex : part4Section.lines.length;
        part4Section.lines.splice(insertIndex, 0, line);
      }
      if (!line.fields.length) {
        line.fields = this.buildPart4Line1Fields(line);
      }
    }
  }

  private upsertPart4Line1Item(
    section: F8964EleSection,
    lineNo: string,
    fields: F8964EleField[],
    label: string,
    formLineKey: string
  ): void {
    const normalizedLineNo = String(lineNo).toLowerCase();
    const existing = section.lines.find(
      line => line.kind === 'part4-line1-item' && String(line.lineNo).toLowerCase() === normalizedLineNo
    );

    if (existing) {
      if (fields.length > 0) {
        existing.fields = fields;
      }
      if (label) {
        existing.label = label;
      }
      if (formLineKey) {
        existing.formLineKey = formLineKey;
      }
      return;
    }

    section.lines.push({
      kind: 'part4-line1-item',
      lineNo: normalizedLineNo,
      label: label || this.resolvePart4Line1ItemLabel(normalizedLineNo, ''),
      fields,
      formLineKey
    });
  }

  /** Legacy combined `part4-line1` rows stored line_no as `1` and broke save — fold into line items. */
  private consolidatePart4Line1Sections(): void {
    for (const section of this.formSections) {
      if (section.mode !== 'part4') {
        continue;
      }

      const combinedLines = section.lines.filter(line => line.kind === 'part4-line1');
      for (const combinedLine of combinedLines) {
        const grouped: Record<'1a' | '1b', F8964EleField[]> = { '1a': [], '1b': [] };
        for (const field of combinedLine.fields) {
          const lineNo = this.deriveLineNoFromAttrib(field.attribName);
          if (lineNo === '1a' || lineNo === '1b') {
            grouped[lineNo].push(field);
          }
        }
        for (const lineNo of ['1a', '1b'] as const) {
          if (grouped[lineNo].length) {
            this.upsertPart4Line1Item(section, lineNo, grouped[lineNo], '', combinedLine.formLineKey);
          }
        }
      }

      if (combinedLines.length) {
        section.lines = section.lines.filter(line => line.kind !== 'part4-line1');
      }
    }
  }

  private buildPart4Line1Fields(line: F8964EleRenderLine): F8964EleField[] {
    const lineNo = String(line.lineNo || '').toLowerCase();
    const attribName = lineNo === '1a'
      ? 'F8964_ELE_IV_Line1a'
      : lineNo === '1b'
        ? 'F8964_ELE_IV_Line1b'
        : '';
    if (!attribName) {
      return [];
    }

    const groupRows = line.formLineKey
      ? this.rawFormRows.filter(row => String(row.FORM_LINE_KEY ?? row.form_line_key ?? '') === line.formLineKey)
      : [];
    const builtFromGroup = this.buildFields(groupRows);
    if (builtFromGroup.length > 0) {
      return builtFromGroup;
    }

    const dataRow = this.rawFormRows.find(row => {
      const { attribName: name } = this.parseAttribValue(row);
      if (!new RegExp(`^${attribName}$`, 'i').test(name)) {
        return false;
      }
      const pt = String(row.PROPERTY_TYPE ?? '');
      return pt !== '2' && pt !== '0' && pt !== '*';
    });
    if (dataRow) {
      return this.buildFields([dataRow]);
    }

    const anchorRow = groupRows[0]
      || this.rawFormRows.find(row => {
        const { attribName: name } = this.parseAttribValue(row);
        return this.isLineDescriptionAttrib(name) && String(row.line_no || '').toLowerCase() === lineNo;
      });

    if (!anchorRow) {
      return [this.createSyntheticPart4Line1Field({}, attribName, lineNo)];
    }

    return [this.createSyntheticPart4Line1Field(anchorRow, attribName, lineNo)];
  }

  private createSyntheticPart4Line1Field(anchorRow: any, attribName: string, lineNo: string): F8964EleField {
    const editType: F8964FieldEditType = 'text';
    const savedRow = this.rawFormRows.find(row => {
      const { attribName: name } = this.parseAttribValue(row);
      return new RegExp(`^${attribName}$`, 'i').test(name);
    });
    const row = savedRow || anchorRow;
    const parsed = this.parseAttribValue(row);

    return {
      attribName: savedRow ? parsed.attribName : attribName,
      lineAttribKey: parsed.lineAttribKey || '',
      objectId: String(row.object_id || anchorRow.object_id || ''),
      propertyType: String(row.PROPERTY_TYPE ?? '7'),
      value: this.extractRowValue(row, editType),
      isChanged: false,
      fieldLabel: ATTRIB_LABELS[attribName] || lineNo,
      isEditableFlag: 'Y',
      editType,
      dropdownOptions: [],
      rowOrder: parseInt(String(row.ROW_CHANGE_INDICATOR ?? row.row_change_indicator ?? '0'), 10) || 0
    };
  }

  private ensurePart4DefaultShareholderEntry(): void {
    const part4Section = this.formSections.find(section => section.mode === 'part4');
    if (!part4Section) {
      return;
    }

    const hasLine2Header = part4Section.lines.some(line => this.isPart4ShareholderHeaderLine(line));
    if (!hasLine2Header) {
      return;
    }

    let shareholderLines = part4Section.lines.filter(line => this.isPart4ShareholderLine(line));
    if (shareholderLines.length === 0) {
      const defaultLine = this.createDefaultPart4ShareholderLine();
      if (defaultLine) {
        const headerIndex = part4Section.lines.findIndex(line => this.isPart4ShareholderHeaderLine(line));
        const insertIndex = headerIndex >= 0 ? headerIndex + 1 : part4Section.lines.length;
        part4Section.lines.splice(insertIndex, 0, defaultLine);
        shareholderLines = [defaultLine];
      }
    }

    if (!shareholderLines.length) {
      this.part4VisibleRowCount = 1;
      return;
    }

    const lastPopulatedIndex = shareholderLines.reduce((lastIndex, line, index) => {
      const hasValue = line.fields.some(field =>
        field.value !== undefined && field.value !== null && String(field.value).trim() !== ''
      );
      return hasValue ? index : lastIndex;
    }, -1);
    this.part4VisibleRowCount = Math.max(1, lastPopulatedIndex + 1);
  }

  private createDefaultPart4ShareholderLine(): F8964EleRenderLine | null {
    if (!this.rawFormRows.length) {
      return null;
    }

    const groups = this.groupByFormLineKey(this.rawFormRows);
    let shareholderGroup = groups.find(group => this.isPart4ShareholderGroup(group));

    if (!shareholderGroup) {
      shareholderGroup = groups.find(group =>
        group.rows.some(row => {
          const { attribName } = this.parseAttribValue(row);
          return /F8964_ELE_IV_Line2[a-e]_/i.test(attribName)
            && String(row.PROPERTY_TYPE) === '7';
        })
      );
    }

    if (!shareholderGroup) {
      shareholderGroup = groups.find(group =>
        group.rows.some(row => /F8964_ELE_IV_LINE2A_/i.test(this.parseAttribValue(row).attribName))
      );
    }

    if (!shareholderGroup) {
      shareholderGroup = groups.find(group =>
        group.rows.some(row => /^F8964_ELE_IV_Line2a_/i.test(this.parseAttribValue(row).attribName))
      );
    }

    if (!shareholderGroup) {
      return null;
    }

    const fields = this.sortPart4Fields(this.buildFields(shareholderGroup.rows));
    if (!fields.length) {
      return null;
    }

    const firstRow = shareholderGroup.rows[0];
    const { attribName } = this.parseAttribValue(firstRow);
    const lineNo = this.deriveLineNoFromAttrib(attribName)
      || this.normalizeLineNo(firstRow.line_no)
      || '2a';

    return {
      kind: 'multi-col',
      lineNo,
      label: '',
      fields,
      formLineKey: shareholderGroup.key
    };
  }

  private isEditableField(row: any): boolean {
    const pt = String(row.PROPERTY_TYPE);
    return pt === '7' || pt === '13' || pt === '10' || pt === '11';
  }

  private resolveRowLabel(row: any): string {
    const { attribName, description } = this.parseAttribValue(row);
    return this.resolveRowDescription(attribName, description);
  }

  /** PDF section/line copy first; API attrib_value description as fallback. */
  private resolveRowDescription(attribName: string, apiDescription?: string): string {
    if (SECTION_LABELS[attribName]) {
      return SECTION_LABELS[attribName];
    }

    if (ATTRIB_LABELS[attribName]) {
      return ATTRIB_LABELS[attribName];
    }

    for (const [key, label] of Object.entries(PART3_ELECTION_LABELS)) {
      if (attribName.includes(`III_NA_${key}`) && !/_Ident_num|_Effec|_Input_che/i.test(attribName)) {
        return label;
      }
    }

    const trimmedApi = (apiDescription || '').trim();
    if (trimmedApi) {
      return this.normalizeCorruptedLabelText(trimmedApi);
    }

    for (const [suffix, label] of Object.entries(FIELD_SUFFIX_LABELS)) {
      if (attribName.replace(/\s/g, '').includes(suffix.replace(/\s/g, ''))) {
        return label;
      }
    }

    return this.cleanAttribName(attribName);
  }

  private resolvePart4Line1ItemLabel(lineNo: string, apiLabel: string): string {
    const attribKey = lineNo === '1a'
      ? 'F8964_ELE_IV_Line1a'
      : lineNo === '1b'
        ? 'F8964_ELE_IV_Line1b'
        : '';
    if (attribKey && ATTRIB_LABELS[attribKey]) {
      return ATTRIB_LABELS[attribKey];
    }
    return this.normalizeCorruptedLabelText(apiLabel);
  }

  /**
   * Backend LINE_DESCRIPTION text sometimes stores apostrophes/dashes as `?`
   * (e.g. "Representative?s", "Election?Regulations").
   */
  private normalizeCorruptedLabelText(text: string): string {
    if (!text) {
      return text;
    }
    return text
      .replace(/(\w)\?s\b/g, "$1's")
      .replace(/\?Yes,\?/gi, '"Yes,"')
      .replace(/\?No,\?/gi, '"No,"')
      .replace(/([a-z])\?([A-Z])/g, '$1\u2014$2');
  }

  private cleanAttribName(attribName: string): string {
    return attribName
      .replace(/^F8964_ELE_/, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private extractPart3LineNo(fields: F8964EleField[], attribName?: string): string {
    const fromAttrib = attribName ? this.deriveLineNoFromAttrib(attribName) : '';
    if (fromAttrib) {
      return fromAttrib;
    }
    for (const field of fields) {
      for (const key of Object.keys(PART3_ELECTION_LABELS)) {
        if (field.attribName.includes(key)) {
          return key.replace('Line', '');
        }
      }
    }
    return '';
  }

  private getPart3ElectionLabel(fields: F8964EleField[], attribName?: string): string {
    if (attribName) {
      for (const [key, label] of Object.entries(PART3_ELECTION_LABELS)) {
        if (attribName.includes(`III_NA_${key}`)) {
          return label;
        }
      }
    }
    for (const field of fields) {
      for (const [key, label] of Object.entries(PART3_ELECTION_LABELS)) {
        if (field.attribName.includes(key)) {
          return label;
        }
      }
    }
    const base = fields.find(f => !PART3_MULTI_SUFFIXES.some(
      s => f.attribName.replace(/\s/g, '').includes(s.replace(/\s/g, ''))
    ));
    return base?.fieldLabel || this.cleanAttribName(base?.attribName || '');
  }

  private sortPart3Fields(fields: F8964EleField[]): F8964EleField[] {
    return [...fields].sort((a, b) => {
      const ai = PART3_FIELD_ORDER.findIndex(k => a.attribName.replace(/\s/g, '').includes(k.replace(/\s/g, '')));
      const bi = PART3_FIELD_ORDER.findIndex(k => b.attribName.replace(/\s/g, '').includes(k.replace(/\s/g, '')));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }

  private sortPart4Fields(fields: F8964EleField[]): F8964EleField[] {
    return [...fields].sort((a, b) => {
      const ai = PART4_FIELD_SORT_ORDER.findIndex(k => a.attribName.toUpperCase().includes(k.toUpperCase()));
      const bi = PART4_FIELD_SORT_ORDER.findIndex(k => b.attribName.toUpperCase().includes(k.toUpperCase()));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }

  private resolveEditableFlag(row: any): string {
    const batchFlag = String(row?.BATCH_FLAG ?? 'N').toUpperCase();
    if (batchFlag === 'Y') {
      return 'N';
    }
    const pt = String(row?.PROPERTY_TYPE ?? '');
    return pt === '2' || pt === '0' ? 'N' : 'Y';
  }

  private isPrincipalActivityCodeField(attribName: string): boolean {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();
    return /F8964_ELE_I_B_LINE1H$/i.test(normalized)
      || /F8964_ELE_I_B_Line1h$/i.test(attribName);
  }

  private isPrincipalActivityDescField(attribName: string): boolean {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();
    return /F8964_ELE_I_B_LINE1I$/i.test(normalized)
      || /F8964_ELE_I_B_Line1i$/i.test(attribName);
  }

  /** Force 8858 field types: 1h = activity code dropdown, 1i = readonly description. */
  private applyPrincipalActivityFieldTypes(fields: F8964EleField[]): void {
    for (const field of fields) {
      if (this.isPrincipalActivityCodeField(field.attribName)) {
        field.editType = 'dropdown';
        field.dropdownOptions = this.resolveDropdownOptions(field.attribName);
      } else if (this.isPrincipalActivityDescField(field.attribName)) {
        field.editType = 'readonly';
      }
    }
  }

  /** Force 8858 field type: functional currency always uses full currencies lookup. */
  private applyFunctionalCurrencyFieldTypes(fields: F8964EleField[]): void {
    for (const field of fields) {
      if (this.isFunctionalCurrencyField(field.attribName)) {
        field.editType = 'dropdown';
        field.dropdownOptions = this.resolveDropdownOptions(field.attribName);
      }
    }
  }

  private isFunctionalCurrencyField(attribName: string): boolean {
    return this.resolveLookupType(attribName) === 'currency';
  }

  private resolveLookupType(attribName: string): F8964LookupType | null {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();

    if (this.isPrincipalActivityCodeField(attribName)) {
      return 'principal-activity-code';
    }
    if (/F8964_ELE_I_A_LINE1G$|F8964_ELE_I_A_LINE2D$|F8964_ELE_I_B_LINE1J$/i.test(normalized)
      || /F8964_ELE_I_A_line1g$|F8964_ELE_I_A_line2d$|F8964_ELE_I_B_Line1j$/i.test(attribName)) {
      return 'currency';
    }
    if (/F8964_ELE_I_A_LINE1F$|F8964_ELE_I_B_LINE1G$/i.test(normalized)
      || /F8964_ELE_I_A_line1f$/i.test(attribName)
      || /F8964_ELE_I_A_line1c\(iv\)$|F8964_ELE_I_A_line2b$|F8964_ELE_I_B_Line1c\(iv\)$/i.test(attribName)
      || this.isCountryField(attribName)) {
      return 'country';
    }
    if (/STATE/i.test(attribName) && !/_F_STATE$/i.test(attribName)) {
      return 'state';
    }
    return null;
  }

  private appendInstructionNotesForAttrib(attribName: string, section: F8964EleSection, formLineKey: string): void {
    const notes = INSTRUCTION_NOTES[attribName];
    if (notes?.length) {
      notes.forEach(note => this.appendInstructionNote(note, section, formLineKey));
    }
  }

  private appendInstructionNote(note: string, section: F8964EleSection, formLineKey: string): void {
    section.lines.push({
      kind: 'instruction-note',
      lineNo: '',
      label: note,
      fields: [],
      formLineKey
    });
  }

  private isDateAttrib(attribName: string): boolean {
    if (/Effec_?\s?(ele|rev)/i.test(attribName.replace(/\s/g, ''))) {
      return true;
    }
    if (/F8964_ELE_I_B_LINE1F$|F8964_ELE_I_B_Line1c\(ii\)|F8964_ELE_I_B_Line1c\(iii\)|F8964_ELE_I_B_Line1e$|F8964_ELE_I_B_Line1f$/i.test(attribName.replace(/\s/g, ''))
      || /F8964_ELE_I_B_Line1c\(ii\)|F8964_ELE_I_B_Line1c\(iii\)|F8964_ELE_I_B_Line1e$|F8964_ELE_I_B_Line1f$/i.test(attribName)) {
      return true;
    }
    return false;
  }

  private isRadioAttrib(attribName: string, propertyType: string): boolean {
    if (String(propertyType) === '13') {
      return true;
    }
    if (attribName === 'F8964_ELE_NA') {
      return true;
    }
    if (/AUTH_OWN_[YN]$/i.test(attribName)) {
      return true;
    }
    if (/F8964_ELE_II_A_LINE1[AB]_[YN]$/i.test(attribName)) {
      return true;
    }
    if (/F8964_ELE_II_A_Line1a$|F8964_ELE_II_A_Line1b$/i.test(attribName)) {
      return true;
    }
    return false;
  }

  private resolveEditType(row: any, attribName: string): F8964FieldEditType {
    const pt = String(row?.PROPERTY_TYPE ?? '');

    if (pt === '2' || pt === '0') {
      return 'readonly';
    }
    if (this.isPrincipalActivityDescField(attribName)) {
      return 'readonly';
    }
    if (this.isPrincipalActivityCodeField(attribName)) {
      return 'dropdown';
    }
    const lookupType = this.resolveLookupType(attribName);
    if (lookupType) {
      return 'dropdown';
    }
    if (this.isRadioAttrib(attribName, pt)) {
      return 'radio';
    }
    if (pt === '11' || /Input_che|III_NA_LINE\d+$/i.test(attribName)) {
      return 'checkbox';
    }
    if (pt === '10' || this.isDateAttrib(attribName)) {
      return 'date';
    }
    if (pt === '9' || /Perc_Sto|PERCENT|_PCT|VOTING_STOCK/i.test(attribName)) {
      return 'number';
    }
    if (pt === '8' || pt === '3' || pt === '4') {
      return 'dropdown';
    }
    if (pt === '7') {
      if (/Input_che/i.test(attribName)) {
        return 'checkbox';
      }
      if (this.isDateAttrib(attribName)) {
        return 'date';
      }
      if (this.resolveLookupType(attribName)) {
        return 'dropdown';
      }
      if (/F8964_ELE_IV_Line1b$/i.test(attribName)) {
        return 'text';
      }
      return 'text';
    }
    return 'text';
  }

  private resolveDropdownOptions(attribName: string): Array<{ code: string; desc: string }> {
    const lookupType = this.resolveLookupType(attribName);
    if (lookupType === 'currency') {
      return (this.currencies || []).map((c: any) => ({
        code: String(c.CURR_CODE ?? ''),
        desc: `${c.CURR_CODE} - ${c.CURR_DESC}`
      }));
    }
    if (lookupType === 'country') {
      return (this.countries || []).map((c: any) => ({
        code: String(c.COUNTRY_CODE ?? ''),
        desc: `${c.COUNTRY_CODE} - ${c.COUNTRY_NAME}`
      }));
    }
    if (lookupType === 'state') {
      return (this.states || []).map((s: any) => ({
        code: String(s.CODE ?? ''),
        desc: `${s.CODE} - ${s.NAME}`
      }));
    }
    if (lookupType === 'principal-activity-code') {
      return (this.principalBusinessActivityList || []).map((a: any) => ({
        code: String(a.ACTIVITY_CODE ?? ''),
        desc: `${a.ACTIVITY_CODE} - ${a.ACTIVITY_DESC}`
      }));
    }
    return [];
  }

  private syncLinkedLookupFieldValues(): void {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        for (const field of line.fields) {
          const lookupType = this.resolveLookupType(field.attribName);
          const fieldValue = this.getDropdownModelValue(field);

          if (lookupType === 'principal-activity-code' && fieldValue) {
            this.principalActivityCode = fieldValue;
          }
          if (lookupType === 'currency' && fieldValue) {
            this.functionalCurrency = fieldValue;
          }
          if (this.isPrincipalActivityDescField(field.attribName) && field.value) {
            this.principalBusinessActivityDesc = String(field.value).trim();
          }
        }
      }
    }

    this.syncPrincipalBusinessActivityDesc();

    for (const section of this.formSections) {
      for (const line of section.lines) {
        for (const field of line.fields) {
          if (this.isPrincipalActivityDescField(field.attribName)) {
            field.value = this.getPrincipalBusinessActivityDesc(this.principalActivityCode)
              || this.principalBusinessActivityDesc
              || field.value;
            continue;
          }

          const lookupType = this.resolveLookupType(field.attribName);
          if (lookupType === 'principal-activity-code' && this.principalActivityCode) {
            field.value = this.principalActivityCode;
          } else if (lookupType === 'currency' && this.functionalCurrency) {
            field.value = this.functionalCurrency;
          }

          if (field.editType === 'dropdown') {
            this.normalizeDropdownValue(field);
          }
        }
      }
    }
  }

  isPrincipalActivityCodeDropdown(field: F8964EleField): boolean {
    return this.isPrincipalActivityCodeField(field.attribName);
  }

  isFunctionalCurrencyDropdown(field: F8964EleField): boolean {
    return this.isFunctionalCurrencyField(field.attribName);
  }

  isPrincipalActivityDescDisplay(field: F8964EleField): boolean {
    return this.isPrincipalActivityDescField(field.attribName);
  }

  getPrincipalActivityCodeModel(field: F8964EleField): string {
    const model = this.getDropdownModelValue(field) || this.principalActivityCode || '';
    return model ? String(model) : '';
  }

  getFunctionalCurrencyModel(field: F8964EleField): string {
    const model = this.getDropdownModelValue(field) || this.functionalCurrency || '';
    return model ? String(model) : '';
  }

  getPrincipalActivityDescDisplay(field: F8964EleField): string {
    const fromCode = this.getPrincipalBusinessActivityDesc(this.principalActivityCode);
    if (fromCode) {
      return fromCode;
    }
    if (field.value !== undefined && field.value !== null && field.value !== '') {
      return String(field.value);
    }
    return this.principalBusinessActivityDesc || '';
  }

  onPrincipalActivityCodeFieldChange(field: F8964EleField, activityCode: string): void {
    field.value = activityCode || '';
    field.isChanged = true;
    this.onPrincipalActivityCodeChange(activityCode);
  }

  onFunctionalCurrencyFieldChange(field: F8964EleField, currencyCode: string): void {
    field.value = currencyCode || '';
    field.isChanged = true;
    this.functionalCurrency = field.value;
    this.syncFunctionalCurrencyFields(field.value, true);
    this.cdr.detectChanges();
  }

  private syncFunctionalCurrencyFields(currencyCode: string, markChanged = false): void {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        for (const field of line.fields) {
          if (this.isFunctionalCurrencyDropdown(field)) {
            field.value = currencyCode;
            if (markChanged) {
              field.isChanged = true;
            }
          }
        }
      }
    }
  }

  private getPrincipalBusinessActivityDesc(activityCode: string): string {
    if (!activityCode) {
      return this.principalBusinessActivityDesc || '';
    }
    const match = this.principalBusinessActivityList.find(
      (a: any) => String(a.ACTIVITY_CODE) === String(activityCode)
    );
    return match?.ACTIVITY_DESC || this.principalBusinessActivityDesc || '';
  }

  private refreshFieldDropdownOptions(): void {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        this.applyPrincipalActivityFieldTypes(line.fields);
        this.applyFunctionalCurrencyFieldTypes(line.fields);
        for (const field of line.fields) {
          if (field.editType === 'dropdown') {
            field.dropdownOptions = this.resolveDropdownOptions(field.attribName);
          }
        }
      }
    }
  }

  private groupByFormLineKey(rows: any[]): { key: string; rows: any[] }[] {
    const rawGroups: { key: string; rows: any[] }[] = [];
    let current: { key: string; rows: any[] } | null = null;

    for (const row of rows) {
      if (String(row.PROPERTY_TYPE) === '*') {
        continue;
      }
      const key = String(row.FORM_LINE_KEY ?? row.form_line_key ?? '');
      if (!key || key === 'undefined') {
        continue;
      }
      if (!current || current.key !== key) {
        current = { key, rows: [] };
        rawGroups.push(current);
      }
      current.rows.push(row);
    }
    return rawGroups;
  }

  private resolveFieldLabel(attribName: string, apiDescription?: string): string {
    const normalized = attribName.replace(/\s/g, '').toUpperCase();
    const sortedSuffixes = Object.keys(FIELD_SUFFIX_LABELS).sort((a, b) => b.length - a.length);
    for (const suffix of sortedSuffixes) {
      if (normalized.includes(suffix.replace(/\s/g, '').toUpperCase())) {
        return FIELD_SUFFIX_LABELS[suffix];
      }
    }
    return this.resolveRowDescription(attribName, apiDescription);
  }

  private buildFields(rows: any[]): F8964EleField[] {
    const fields = rows
      .filter((r: any) => {
        const { attribName } = this.parseAttribValue(r);
        return !this.isLabelOnlyAttrib(attribName);
      })
      .map((r: any) => {
        const { attribName, lineAttribKey, description } = this.parseAttribValue(r);
        const editType = this.resolveEditType(r, attribName);
        const field: F8964EleField = {
          attribName,
          lineAttribKey,
          objectId: String(r.object_id || ''),
          propertyType: String(r.PROPERTY_TYPE),
          value: this.extractRowValue(r, editType),
          isChanged: false,
          fieldLabel: this.resolveFieldLabel(attribName, description),
          isEditableFlag: this.resolveEditableFlag(r),
          editType,
          dropdownOptions: this.resolveDropdownOptions(attribName),
          rowOrder: parseInt(String(r.ROW_CHANGE_INDICATOR ?? r.row_change_indicator ?? '0'), 10) || 0
        };
        return field;
      });
    return this.finalizeBuiltFields(fields);
  }

  private finalizeBuiltFields(fields: F8964EleField[]): F8964EleField[] {
    this.applyPrincipalActivityFieldTypes(fields);
    this.applyFunctionalCurrencyFieldTypes(fields);
    return fields;
  }

  private extractRowValue(row: any, editType?: F8964FieldEditType): any {
    let raw = row?.VALUE;
    if (raw === undefined || raw === null || raw === 'null') {
      raw = row?.value;
    }
    if (raw === undefined || raw === null || raw === 'null') {
      raw = row?.Attribute_Value;
    }
    if (raw === undefined || raw === null || raw === 'null') {
      return '';
    }
    if (typeof raw === 'object') {
      return raw.COUNTRY_CODE ?? raw.CURR_CODE ?? raw.ACTIVITY_CODE ?? raw.code ?? raw.CODE ?? raw;
    }
    if (editType === 'dropdown' || editType === 'radio') {
      return String(raw).trim();
    }
    return raw;
  }

  private normalizeAllFieldBindings(): void {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        this.normalizeRadioFields(line.fields);
        for (const field of line.fields) {
          if (field.editType === 'dropdown') {
            this.normalizeDropdownValue(field);
          }
        }
      }
    }
  }

  private normalizeRadioFields(fields: F8964EleField[]): void {
    const processed = new Set<string>();

    for (const field of fields) {
      if (field.editType !== 'radio' || processed.has(field.attribName)) {
        continue;
      }

      const primary = this.getRadioPrimaryField(field, fields);
      const nField = this.getPairedRadioField(fields, primary);
      processed.add(primary.attribName);
      if (nField) {
        processed.add(nField.attribName);
      }

      const yVal = String(primary.value ?? '').trim().toUpperCase();
      const nVal = String(nField?.value ?? '').trim().toUpperCase();

      if (yVal === 'Y' || yVal === 'TRUE' || yVal === '1') {
        primary.value = 'Y';
        if (nField) {
          nField.value = '';
        }
      } else if (nVal === 'N' || yVal === 'N' || nVal === 'FALSE' || nVal === '0') {
        primary.value = '';
        if (nField) {
          nField.value = 'N';
        } else {
          primary.value = 'N';
        }
      } else {
        primary.value = '';
        if (nField) {
          nField.value = '';
        }
      }
    }
  }

  private normalizeDropdownValue(field: F8964EleField): void {
    let raw = field.value;
    if (raw === undefined || raw === null || raw === 'null') {
      field.value = '';
      return;
    }
    if (typeof raw === 'object') {
      raw = raw.COUNTRY_CODE ?? raw.CURR_CODE ?? raw.ACTIVITY_CODE ?? raw.code ?? raw.CODE ?? '';
    }

    const strVal = String(raw).trim();
    if (!strVal) {
      field.value = '';
      return;
    }

    const options = field.dropdownOptions || [];
    if (!options.length) {
      field.value = strVal;
      return;
    }

    const match = options.find(opt => String(opt.code) === strVal)
      || options.find(opt => String(opt.code).toUpperCase() === strVal.toUpperCase())
      || options.find(opt => String(opt.desc || '').toUpperCase().startsWith(`${strVal.toUpperCase()} -`));

    field.value = match ? String(match.code) : strVal;
  }

  getRadioPrimaryField(field: F8964EleField, allFields: F8964EleField[]): F8964EleField {
    if (/_N$/i.test(field.attribName)) {
      return this.getPairedRadioField(allFields, field) || field;
    }
    return field;
  }

  getRadioGroupValue(field: F8964EleField, allFields: F8964EleField[]): string {
    const primary = this.getRadioPrimaryField(field, allFields);
    const nField = this.getPairedRadioField(allFields, primary);
    const yVal = String(primary.value ?? '').trim().toUpperCase();
    const nVal = String(nField?.value ?? '').trim().toUpperCase();

    if (yVal === 'Y') {
      return 'Y';
    }
    if (nVal === 'N' || yVal === 'N') {
      return 'N';
    }
    return '';
  }

  getDropdownModelValue(field: F8964EleField): string {
    const value = field.value;
    if (value === undefined || value === null || value === 'null') {
      return '';
    }
    if (typeof value === 'object') {
      const code = value.COUNTRY_CODE ?? value.CURR_CODE ?? value.ACTIVITY_CODE ?? value.code ?? value.CODE;
      return code !== undefined && code !== null ? String(code) : '';
    }
    return String(value);
  }

  onRadioGroupChange(field: F8964EleField, value: string, allFields: F8964EleField[] = []): void {
    this.onRadioChange(this.getRadioPrimaryField(field, allFields), value, allFields);
  }

  private normalizeLineNo(lineNo: any): string {
    if (lineNo === 'N/A' || lineNo === undefined || lineNo === null) {
      return '';
    }
    const normalized = String(lineNo);
    if (/^Part\s*-/i.test(normalized)) {
      return '';
    }
    return normalized;
  }

  onFieldChange(field: F8964EleField): void {
    field.isChanged = true;
  }

  onRadioChange(field: F8964EleField, value: string, allFields: F8964EleField[] = []): void {
    const primary = this.getRadioPrimaryField(field, allFields);
    const nField = this.getPairedRadioField(allFields, primary);
    const selected = String(value || '').toUpperCase();

    if (selected === 'Y') {
      primary.value = 'Y';
      if (nField) {
        nField.value = '';
        nField.isChanged = true;
      }
    } else if (selected === 'N') {
      primary.value = '';
      if (nField) {
        nField.value = 'N';
        nField.isChanged = true;
      } else {
        primary.value = 'N';
      }
    } else {
      primary.value = '';
      if (nField) {
        nField.value = '';
        nField.isChanged = true;
      }
    }

    primary.isChanged = true;
    this.cdr.detectChanges();
  }

  onCheckboxChange(field: F8964EleField, event: Event, _lineFields: F8964EleField[] = []): void {
    const checked = (event.target as HTMLInputElement).checked;
    field.value = checked ? '1' : '0';
    field.isChanged = true;
    this.cdr.detectChanges();
  }

  onDropdownChange(field: F8964EleField, value: string, lineFields: F8964EleField[] = []): void {
    field.value = value === undefined || value === null ? '' : String(value);
    field.isChanged = true;

    const lookupType = this.resolveLookupType(field.attribName);
    if (lookupType === 'principal-activity-code') {
      this.onPrincipalActivityCodeFieldChange(field, field.value);
      return;
    }
    if (lookupType === 'currency') {
      this.onFunctionalCurrencyFieldChange(field, field.value);
      return;
    }
    if (lookupType === 'country' && this.isCountryField(field.attribName)) {
      const contextFields = lineFields.length ? lineFields : this.findLineFieldsForField(field);
      if (contextFields.length) {
        this.updateAddressModeFromCountry(contextFields, field.value);
      }
    }

    this.cdr.detectChanges();
  }

  private findLineFieldsForField(field: F8964EleField): F8964EleField[] {
    for (const section of this.formSections) {
      for (const line of section.lines) {
        if (line.fields?.some(f => f.attribName === field.attribName && f.objectId === field.objectId)) {
          return line.fields;
        }
      }
    }
    return [];
  }

  private getAttributeValueForSave(field: F8964EleField): any {
    const raw = field.value;
    if (raw === undefined || raw === null || raw === 'null') {
      return null;
    }
    if (typeof raw === 'object') {
      const code = raw.COUNTRY_CODE ?? raw.CURR_CODE ?? raw.ACTIVITY_CODE ?? raw.code ?? raw.CODE;
      return code !== undefined && code !== null && String(code).length > 0 ? code : null;
    }
    const text = String(raw).trim();
    return text.length > 0 ? text : null;
  }

  private resolveJcdKey(): any {
    return this.rowData?.JCD_KEY
      ?? this.filterParams?.jcd_key
      ?? this.globalServiceWrapper?.globalParams?.jcd_key;
  }

  save(): void {
    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    const part4ValidationError = this.validatePart4BeforeSave();
    if (part4ValidationError) {
      this.snackbar.openSnackBar(part4ValidationError, 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const scheduleSettings = [{
      tax_year: this.getTaxYear(),
      scenario: String(
        this.filterParams?.scenario
        ?? this.rowData?.SCENARIO
        ?? this.globalServiceWrapper?.globalParams?.scenario
        ?? ''
      ),
      jcd_key: String(this.resolveJcdKey()),
      combination_key: String(this.rowData?.COMBINATION_KEY ?? '')
    }];

    const editRows = this.buildEditRows(scheduleSettings[0].tax_year, scheduleSettings[0].combination_key);
    const removeRows = this.buildRemoveRows(scheduleSettings[0].tax_year, scheduleSettings[0].combination_key);

    const scheduleDetails = {
      addrows: [] as any[],
      editrows: editRows,
      removerows: removeRows
    };

    this.f8964EleService.saveForm(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalServiceWrapper,
      this.filterParams,
      this.rowData
    ).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result && result.errorMessage && result.errorMessage !== 'null') {
          this.snackbar.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.part4DeletedRowIndices.clear();
          this.snackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.snackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  private buildEditRows(taxYear: any, combinationKey?: any): any[] {
    const editRows: any[] = [];
    const savedFieldKeys = new Set<string>();
    const saveCombinationKey = combinationKey
      ?? this.rowData?.HO_COMBINATION_KEY
      ?? this.rowData?.COMBINATION_KEY;

    for (const section of this.formSections) {
      for (const line of section.lines) {
        if (this.isHiddenPart4ShareholderLine(line)) {
          continue;
        }
        for (const field of line.fields) {
          if (!field.isChanged) {
            continue;
          }
          if (!field.objectId || !field.lineAttribKey) {
            continue;
          }

          const fieldKey = `${field.lineAttribKey}|${field.attribName}|${field.objectId}`;
          if (savedFieldKeys.has(fieldKey)) {
            continue;
          }
          savedFieldKeys.add(fieldKey);

          const objParts = field.objectId.split('~');
          const value = this.getAttributeValueForSave(field);

          editRows.push([{
            tax_year: taxYear,
            trans_type_key: objParts[1],
            combination_key: saveCombinationKey,
            group_obj_key: this.rowData?.GROUP_OBJ_KEY,
            form_key: objParts[6],
            line_no: this.resolveSaveLineNo(field, line),
            occurence: this.resolveSaveOccurrence(objParts),
            line_attrib_key: field.lineAttribKey,
            Attribute_Name: field.attribName,
            Attribute_Value: value,
            trans_dtls_key: (objParts[3] === '0' ? null : objParts[3]),
            trans_status: 'A'
          }]);
        }
      }
    }

    return editRows;
  }

  private buildRemoveRows(taxYear: unknown, combinationKey?: unknown): unknown[] {
    const removeRows: unknown[] = [];
    const savedFieldKeys = new Set<string>();
    const saveCombinationKey = combinationKey
      ?? this.rowData?.HO_COMBINATION_KEY
      ?? this.rowData?.COMBINATION_KEY;
    const shareholderLines = this.getPart4ShareholderLines();

    for (const rowIndex of this.part4DeletedRowIndices) {
      const line = shareholderLines[rowIndex];
      if (!line || !this.part4ShareholderRowHasPersistedData(line)) {
        continue;
      }

      for (const field of line.fields) {
        if (!field.objectId || !field.lineAttribKey) {
          continue;
        }

        const objParts = field.objectId.split('~');
        const transDtlsKey = objParts[3];
        if (!transDtlsKey || transDtlsKey === '0') {
          continue;
        }

        const fieldKey = `${field.lineAttribKey}|${field.attribName}|${field.objectId}`;
        if (savedFieldKeys.has(fieldKey)) {
          continue;
        }
        savedFieldKeys.add(fieldKey);

        removeRows.push([{
          tax_year: taxYear,
          trans_type_key: objParts[1],
          combination_key: saveCombinationKey,
          group_obj_key: this.rowData?.GROUP_OBJ_KEY,
          form_key: objParts[6],
          line_no: this.resolveSaveLineNo(field, line),
          occurence: this.resolveSaveOccurrence(objParts),
          line_attrib_key: field.lineAttribKey,
          Attribute_Name: field.attribName,
          Attribute_Value: null,
          trans_dtls_key: transDtlsKey,
          trans_status: 'A'
        }]);
      }
    }

    return removeRows;
  }

  private isHiddenPart4ShareholderLine(line: F8964EleRenderLine): boolean {
    if (!this.isPart4ShareholderLine(line)) {
      return false;
    }
    const rowIndex = this.getPart4ShareholderLines().indexOf(line);
    return rowIndex > -1 && this.part4HiddenRowIndices.has(rowIndex);
  }

  private part4ShareholderRowHasPersistedData(line: F8964EleRenderLine): boolean {
    return line.fields.some(field => {
      if (!field.objectId) {
        return false;
      }
      const transDtlsKey = field.objectId.split('~')[3];
      return !!transDtlsKey && transDtlsKey !== '0';
    });
  }

  private resolveSaveOccurrence(objParts: string[]): string {
    const occurrence = objParts[4];
    if (!occurrence || occurrence === '0') {
      return '0';
    }
    return occurrence;
  }

  /**
   * Preserve API line_no for save (e.g. "N/A" for AUTH_OWN radios). Display uses
   * normalizeLineNo() which strips N/A — do not use that here.
   */
  private resolveSaveLineNo(field: F8964EleField, line: F8964EleRenderLine): string | null {
    const fromRaw = this.getRawLineNoForField(field);
    if (fromRaw !== null) {
      return fromRaw;
    }
    if (line.lineNo) {
      return line.lineNo;
    }
    return this.deriveLineNoFromAttrib(field.attribName) || null;
  }

  private getRawLineNoForField(field: F8964EleField): string | null {
    const match = this.rawFormRows.find(row => {
      if (String(row.PROPERTY_TYPE) === '*') {
        return false;
      }
      const parsed = this.parseAttribValue(row);
      return parsed.lineAttribKey === field.lineAttribKey
        && parsed.attribName.toUpperCase() === field.attribName.toUpperCase();
    });
    if (!match) {
      return null;
    }
    const lineNo = match.line_no ?? match.LINE_NO;
    if (lineNo === undefined || lineNo === null) {
      return null;
    }
    const text = String(lineNo).trim();
    return text.length > 0 ? text : null;
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.applyMinimiseShellLayout();
    this.cdr.detectChanges();
  }

  onSpaceActivate(
    event: Event,
    action: 'minimise' | 'cancel' | 'addRow' | 'deleteRow',
    line?: F8964EleRenderLine
  ): void {
    event.preventDefault();
    if (action === 'minimise') {
      this.toggleMinimise();
      return;
    }
    if (action === 'cancel') {
      this.cancel();
      return;
    }
    if (action === 'addRow') {
      this.addPart4ShareholderRow();
      return;
    }
    if (line) {
      this.deletePart4ShareholderRow(line);
    }
  }

  /**
   * MatDialog sets height: 90vh on `.cdk-overlay-pane` (registry dialogHeight).
   * CSS on `.mat-dialog-container` alone cannot collapse the shell — mirror
   * `users-component` dialogRef.updateSize(..., 'auto') via the overlay pane.
   */
  private applyMinimiseShellLayout(): void {
    const host = this.elementRef.nativeElement;
    const overlayPane = host.closest('.cdk-overlay-pane') as HTMLElement | null;

    if (this.minimise) {
      host.style.flex = '0 0 auto';
      host.style.height = 'auto';
      host.style.minHeight = '0';
      if (overlayPane) {
        overlayPane.style.height = 'auto';
        overlayPane.style.maxHeight = 'none';
      }
      return;
    }

    host.style.flex = '1 1 auto';
    host.style.height = '';
    host.style.minHeight = '0';
    if (overlayPane) {
      overlayPane.style.height = this.defaultOverlayHeight;
      overlayPane.style.maxHeight = '';
    }
  }

  private isPart4ShareholderRowEmpty(line: F8964EleRenderLine): boolean {
    return !line.fields.some(field => String(field.value ?? '').trim() !== '');
  }

  /**
   * Part IV Line 2: one default empty row is allowed; any row with data (or
   * additional visible rows) must have every editable column filled.
   */
  private validatePart4BeforeSave(): string | null {
    const visibleLines = this.getPart4ShareholderLines().filter(line => this.isPart4RowVisible(line));
    if (!visibleLines.length) {
      return null;
    }

    if (visibleLines.length === 1 && this.isPart4ShareholderRowEmpty(visibleLines[0])) {
      return null;
    }

    for (const line of visibleLines) {
      if (this.isPart4ShareholderRowEmpty(line)) {
        return `Please complete all columns for line ${line.lineNo} or delete the empty row.`;
      }

      for (const colKey of PART4_FIELD_ORDER) {
        const editableFields = this.getPart4FieldsForColumn(line.fields, colKey)
          .filter(field => field.isEditableFlag !== 'N');
        if (!editableFields.length) {
          continue;
        }
        const hasMissing = editableFields.some(field => !String(field.value ?? '').trim());
        if (hasMissing) {
          return `Please fill "${this.getPart4ColumnHeader(colKey)}" for line ${line.lineNo}.`;
        }
      }
    }

    return null;
  }

  reset(): void {
    this.crudLoading = false;
    this.part4HiddenRowIndices.clear();
    this.part4DeletedRowIndices.clear();
    this.formSections = JSON.parse(JSON.stringify(this.originalFormSections));
    this.consolidatePart4Line1Sections();
    this.ensurePart4Line1Fields();
    this.ensurePart4DefaultShareholderEntry();
    this.initializeAllAddressModes();
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
